// ===================================================================
// HMGP — Project Workspace
// Tabbed detail page for HMGP mitigation projects
// Route: /pagrants/hmgp/projects/:projectId
// ===================================================================

import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Textarea, Modal, Tabs, SimpleGrid, ActionIcon, Loader, Center, Table,
  ThemeIcon, Progress, Tooltip,
} from '@mantine/core';
import {
  IconArrowLeft, IconEdit, IconTrash, IconPlus, IconCheck,
  IconClock, IconMinus, IconShieldCheck, IconLeaf, IconDroplet,
  IconBuildingBank, IconHome,
  IconFileText, IconTimeline, IconNotes, IconCash, IconChartBar,
  IconDeviceFloppy, IconX,
  IconListCheck, IconUpload, IconCheckbox, IconCircle,
  IconPhoto, IconSubtask, IconCamera,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useHMGPStore } from '../../../store/hmgpStore';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useAuthStore } from '../../../store/authStore';
import {
  HMGP_PROJECT_TYPE_LABELS, HMGP_PROJECT_TYPE_COLORS,
  HMGP_PHASE_LABELS, HMGP_PHASE_COLORS,
  HMGP_STATUS_LABELS,
  type HMGPProjectType, type HMGPPhase, type HMGPProject,
  type PAProjectCost, type PADocument, type PATimelineEvent, type PANote,
  type PALaborRecord, type PAEquipRecord, type PAMaterial, type PAContract,
  type PARentedEquip, type PADonatedResource,
} from '../../../types/pagrants';

import { fmtUSD as fmt, num, formatCurrency, stripCurrency } from '../../../lib/formatUtils';
import { TABLES, fetchTable } from '../../../lib/api-switch';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';

const PROJECT_TYPE_OPTIONS = Object.entries(HMGP_PROJECT_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));
const PHASE_OPTIONS = Object.entries(HMGP_PHASE_LABELS).map(([v, l]) => ({ value: v, label: l }));
const STATUS_OPTIONS = Object.entries(HMGP_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }));

const BCA_METHODOLOGY_OPTIONS = [
  { value: 'fema_bca_toolkit', label: 'FEMA BCA Toolkit' },
  { value: 'pre_calculated', label: 'Pre-Calculated Benefits' },
  { value: 'streamlined', label: 'Streamlined BCA' },
];

const NEPA_LEVEL_OPTIONS = [
  { value: 'CE', label: 'Categorical Exclusion (CE)' },
  { value: 'EA', label: 'Environmental Assessment (EA)' },
  { value: 'EIS', label: 'Environmental Impact Statement (EIS)' },
];

const EHP_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'not_required', label: 'Not Required' },
];

const COST_TYPE_OPTIONS = [
  { value: 'construction', label: 'Construction' },
  { value: 'engineering', label: 'Engineering & Design' },
  { value: 'environmental', label: 'Environmental Review' },
  { value: 'permitting', label: 'Permitting & Fees' },
  { value: 'property_acquisition', label: 'Property Acquisition' },
  { value: 'demolition', label: 'Demolition & Removal' },
  { value: 'relocation', label: 'Relocation Assistance' },
  { value: 'project_management', label: 'Project Management' },
  { value: 'inspection', label: 'Inspection & QA' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'materials', label: 'Materials' },
  { value: 'labor', label: 'Labor' },
  { value: 'contingency', label: 'Contingency' },
  { value: 'other', label: 'Other' },
];

// ── HMGP Project Checklist Items ────────────────────────────
interface ChecklistItem {
  key: string;
  label: string;
  category: 'document' | 'form' | 'review' | 'milestone';
  description: string;
  required: boolean;
  forTypes?: string[]; // Only show for these project types (if undefined, show for all)
}

const HMGP_PROJECT_CHECKLIST_FALLBACK: ChecklistItem[] = [
  // Documents
  { key: 'subapplication', label: 'Subapplication Package', category: 'document', description: 'Complete HMGP subapplication with problem description, proposed activity, and cost estimate', required: true },
  { key: 'bca_report', label: 'Benefit-Cost Analysis Report', category: 'document', description: 'FEMA BCA Toolkit output or pre-calculated benefits documentation', required: true },
  { key: 'scope_of_work', label: 'Scope of Work', category: 'document', description: 'Detailed scope including design specifications, quantities, materials, and implementation schedule', required: true },
  { key: 'cost_estimate', label: 'Detailed Cost Estimate', category: 'document', description: 'Line-item cost estimate with supporting quotes, engineer estimates, or historical costs', required: true },
  { key: 'property_appraisals', label: 'Property Appraisals', category: 'document', description: 'Pre-disaster fair market value appraisals for each property', required: true, forTypes: ['acquisition', 'elevation'] },
  { key: 'title_search', label: 'Title Search / Deed', category: 'document', description: 'Property title search and current deed for each affected property', required: true, forTypes: ['acquisition', 'elevation'] },
  { key: 'engineering_design', label: 'Engineering Plans / Design', category: 'document', description: 'Stamped engineering drawings, structural calculations, or architectural plans', required: false },
  { key: 'flood_insurance', label: 'Flood Insurance Documentation', category: 'document', description: 'Proof of flood insurance, policy declarations, or NFIP compliance documentation', required: false, forTypes: ['acquisition', 'elevation', 'drainage', 'localized_flood_control'] },
  { key: 'photos', label: 'Site Photos (Pre-Mitigation)', category: 'document', description: 'Photographs documenting current conditions and proposed mitigation area', required: true },
  { key: 'map_gis', label: 'Project Area Map / GIS Data', category: 'document', description: 'Maps showing project boundaries, FIRM zones, structures, and mitigation footprint', required: true },
  { key: 'procurement_docs', label: 'Procurement Documentation', category: 'document', description: 'Bid solicitations, proposals, contracts, and 2 CFR 200 compliance records', required: false },
  { key: 'maintenance_agreement', label: 'Maintenance Agreement', category: 'document', description: 'Long-term maintenance plan and commitment for mitigation measures', required: false },
  // Forms
  { key: 'sf424', label: 'SF-424 Application for Federal Assistance', category: 'form', description: 'Standard federal grant application form', required: true },
  { key: 'assurances', label: 'SF-424B/D Assurances', category: 'form', description: 'Non-construction (B) or construction (D) assurances for federal assistance', required: true },
  { key: 'applicant_certification', label: 'Applicant Certification', category: 'form', description: 'Signed certification of compliance, environmental review, and non-duplication', required: true },
  { key: 'deed_restriction', label: 'Deed Restriction Template', category: 'form', description: 'Open space deed restriction for acquired properties', required: true, forTypes: ['acquisition'] },
  // Reviews
  { key: 'ehp_review', label: 'EHP / NEPA Review', category: 'review', description: 'Environmental and Historic Preservation review (NEPA, Section 106, floodplain, ESA)', required: true },
  { key: 'bca_validation', label: 'BCA Validated (BCR >= 1.0)', category: 'review', description: 'State or FEMA review confirming benefit-cost ratio meets cost-effectiveness threshold', required: true },
  { key: 'state_review', label: 'State/Grantee Review Complete', category: 'review', description: 'State emergency management agency review and priority ranking', required: true },
  { key: 'fema_review', label: 'FEMA Regional Review', category: 'review', description: 'FEMA Regional Administrator review and selection decision', required: false },
  { key: 'cost_review', label: 'Cost Reasonableness Review', category: 'review', description: 'Independent review confirming project costs are reasonable and well-documented', required: true },
  // Milestones
  { key: 'subapp_submitted', label: 'Subapplication Submitted', category: 'milestone', description: 'Subapplication submitted to state for review', required: true },
  { key: 'state_prioritized', label: 'State Prioritized', category: 'milestone', description: 'State has ranked and prioritized this subapplication', required: true },
  { key: 'fema_selected', label: 'FEMA Selected for Funding', category: 'milestone', description: 'FEMA has selected this project for HMGP funding', required: false },
  { key: 'funds_obligated', label: 'Funds Obligated', category: 'milestone', description: 'Federal funds have been obligated for this project', required: false },
  { key: 'work_completed', label: 'Mitigation Work Completed', category: 'milestone', description: 'All mitigation work has been physically completed and verified', required: false },
  { key: 'closeout_submitted', label: 'Closeout Package Submitted', category: 'milestone', description: 'Final documentation, certifications, and financial reconciliation submitted', required: false },
];

const CHECKLIST_CATEGORY_COLORS: Record<string, string> = {
  document: 'blue', form: 'violet', review: 'orange', milestone: 'green',
};
const CHECKLIST_CATEGORY_LABELS: Record<string, string> = {
  document: 'Document', form: 'Form', review: 'Review', milestone: 'Milestone',
};

// DOC_TYPE_OPTIONS moved into component as useMemo (depends on dynamic checklist)

const EVENT_TYPE_OPTIONS = [
  { value: 'milestone', label: 'Milestone' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'submission', label: 'Submission' },
  { value: 'approval', label: 'Approval' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'task', label: 'Task' },
];

// HMGP green theme
const HMGP_GREEN = '#059669';

export default function HMGPProjectWorkspace() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);

  // ── HMGP Store ──────────────────────────────────────────────
  const { projects, fetchProjects, updateProject, loading: hmgpLoading } = useHMGPStore();

  // ── PA Store (shared tables) ────────────────────────────────
  const {
    organizations,
    projectCosts, fetchProjectCosts,
    createProjectCost, updateProjectCost, deleteProjectCost,
    documents, fetchDocuments,
    createDocument, deleteDocument,
    timelineEvents, fetchTimeline,
    createTimelineEvent, deleteTimelineEvent,
    notes, fetchNotes,
    createNote, deleteNote,
    laborRecords, fetchLabor, createLabor, updateLabor, deleteLabor,
    equipRecords, fetchEquipment, createEquip, updateEquip, deleteEquip,
    materials, fetchMaterials, createMaterial, updateMaterial, deleteMaterial,
    contracts, fetchContracts, createContract, updateContract, deleteContract,
    rentedEquip, fetchRented, createRented, updateRented, deleteRented,
    donatedResources, fetchDonated, createDonated, updateDonated, deleteDonated,
  } = usePAGrantsStore();

  // ── Init ────────────────────────────────────────────────────
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    Promise.all([
      fetchProjects(), fetchProjectCosts(), fetchDocuments(), fetchTimeline(), fetchNotes(),
      fetchLabor(), fetchEquipment(), fetchMaterials(), fetchContracts(), fetchRented(), fetchDonated(),
    ]).finally(() => setLoaded(true));
  }, []);

  // ── Resolve project ─────────────────────────────────────────
  const project = useMemo(() => {
    if (!projectId) return null;
    return projects.find(p => String(p.Rid) === projectId) || null;
  }, [projects, projectId]);

  const orgName = useMemo(() => {
    if (!project) return '';
    const org = organizations.find(o => String(o.Rid) === project.organizationId);
    return org?.orgName || '';
  }, [project, organizations]);

  // ── Filtered shared data ────────────────────────────────────
  const myCosts = useMemo(() => projectCosts.filter(c => c.projectId === projectId), [projectCosts, projectId]);
  const myLabor = useMemo(() => laborRecords.filter(l => l.projectId === projectId), [laborRecords, projectId]);
  const myEquip = useMemo(() => equipRecords.filter(e => e.projectId === projectId), [equipRecords, projectId]);
  const myMaterials = useMemo(() => materials.filter(m => m.projectId === projectId), [materials, projectId]);
  const myContracts = useMemo(() => contracts.filter(c => c.projectId === projectId), [contracts, projectId]);
  const myRented = useMemo(() => rentedEquip.filter(r => r.projectId === projectId), [rentedEquip, projectId]);
  const myDonated = useMemo(() => donatedResources.filter(d => d.projectId === projectId), [donatedResources, projectId]);
  const myDocs = useMemo(() => documents.filter(d => d.projectId === projectId), [documents, projectId]);
  const myTimeline = useMemo(() => timelineEvents.filter(t => t.projectId === projectId).sort((a, b) =>
    (b.eventDate || '').localeCompare(a.eventDate || '')), [timelineEvents, projectId]);
  const myNotes = useMemo(() => notes.filter(n => n.parentId === projectId).sort((a, b) =>
    (b.createdDate || '').localeCompare(a.createdDate || '')), [notes, projectId]);

  // ── Dynamic checklist from database ─────────────────────────
  const [dbChecklist, setDbChecklist] = useState<ChecklistItem[]>(HMGP_PROJECT_CHECKLIST_FALLBACK);
  useEffect(() => {
    (async () => {
      try {
        const rows = await fetchTable<{
          Rid: number; itemKey: string; label: string; category: string;
          description: string; required: string; sortOrder: string;
          isActive: string; programType: string;
        }>(TABLES.paChecklistTemplates);
        const hmgpItems = rows
          .filter(r => r.isActive === 'true' && (r.programType === 'hmgp' || r.programType === 'both'))
          .sort((a, b) => (parseInt(a.sortOrder) || 0) - (parseInt(b.sortOrder) || 0))
          .map(r => {
            // Parse forTypes from ||TYPES:x,y|| marker in description
            const typesMatch = r.description?.match(/\|\|TYPES:([^|]+)\|\|/);
            const cleanDesc = (r.description || '').replace(/\|\|TYPES:[^|]+\|\|/, '').trim();
            return {
              key: r.itemKey,
              label: r.label,
              category: r.category as ChecklistItem['category'],
              description: cleanDesc,
              required: r.required === 'true',
              forTypes: typesMatch ? typesMatch[1].split(',') : undefined,
            };
          });
        if (hmgpItems.length > 0) setDbChecklist(hmgpItems);
      } catch { /* fallback to hardcoded */ }
    })();
  }, []);

  // ── Checklist completion tracking ────────────────────────────
  const checklistItems = useMemo(() =>
    dbChecklist.filter(c => !c.forTypes || c.forTypes.includes(project?.projectType || '')),
    [dbChecklist, project?.projectType]
  );

  const DOC_TYPE_OPTIONS = useMemo(() => [
    ...checklistItems
      .filter(c => c.category === 'document')
      .map(c => ({ value: c.key, label: c.label })),
    { value: 'correspondence', label: 'Correspondence' },
    { value: 'other', label: 'Other' },
  ], [checklistItems]);

  const checklistStatus = useMemo(() => {
    const map = new Map<string, { done: boolean; date?: string; by?: string }>();
    for (const item of checklistItems) {
      const event = myTimeline.find(t =>
        t.description?.includes(`[checklist:${item.key}]`)
      );
      const doc = myDocs.find(d => d.tags === item.key);
      const done = !!(event || doc);
      map.set(item.key, {
        done,
        date: event?.eventDate || doc?.uploadDate || undefined,
        by: event?.creatorId || doc?.uploadedBy || undefined,
      });
    }
    return map;
  }, [checklistItems, myTimeline, myDocs]);

  const checklistDone = useMemo(() =>
    checklistItems.filter(c => checklistStatus.get(c.key)?.done).length,
    [checklistItems, checklistStatus]
  );
  const checklistRequired = checklistItems.filter(c => c.required).length;
  const checklistRequiredDone = useMemo(() =>
    checklistItems.filter(c => c.required && checklistStatus.get(c.key)?.done).length,
    [checklistItems, checklistStatus]
  );

  // ── Checklist state ──────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('');
  const [checkingItem, setCheckingItem] = useState<ChecklistItem | null>(null);
  const [checkNotes, setCheckNotes] = useState('');

  // ── Edit modal state ────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, string>>({});

  // ── Loading / Not Found ─────────────────────────────────────
  if (!loaded || hmgpLoading) {
    return <Center h={400}><Loader color={HMGP_GREEN} size="lg" /></Center>;
  }
  if (!project) {
    return (
      <Stack align="center" mt={80}>
        <Text size="xl" fw={600}>Project Not Found</Text>
        <Text c="dimmed">No HMGP project matches ID "{projectId}".</Text>
        <Button leftSection={<IconArrowLeft size={16} />} variant="light" color="green"
          onClick={() => navigate('/pagrants/hmgp/projects')}>Back to Projects</Button>
      </Stack>
    );
  }

  // ── Derived values ──────────────────────────────────────────
  const estCost = num(project.estimatedCost);
  const fedPct = num(project.federalSharePct) || 75;
  const fedShare = num(project.federalShareAmount) || estCost * (fedPct / 100);
  const applicantMatch = num(project.applicantShareAmount) || estCost - fedShare;
  const awarded = num(project.awardAmount);
  const drawn = num(project.drawdownAmount);
  const pType = project.projectType as HMGPProjectType;
  const phase = project.phase as HMGPPhase;
  const isPropertyType = pType === 'acquisition' || pType === 'elevation';

  // ── Edit helpers ────────────────────────────────────────────
  function openEdit() {
    setEditForm({
      projectTitle: project!.projectTitle || '',
      projectType: project!.projectType || '',
      phase: project!.phase || 'subapplication',
      status: project!.status || 'draft',
      estimatedCost: project!.estimatedCost || '',
      federalSharePct: project!.federalSharePct || '75',
      awardAmount: project!.awardAmount || '',
      problemDescription: project!.problemDescription || '',
      scopeOfWork: project!.scopeOfWork || '',
      projectAddress: project!.projectAddress || '',
      projectCity: project!.projectCity || '',
      projectState: project!.projectState || '',
      projectZip: project!.projectZip || '',
      performancePeriodStart: project!.performancePeriodStart || '',
      performancePeriodEnd: project!.performancePeriodEnd || '',
      assignedSpecialist: project!.assignedSpecialist || '',
      notes: project!.notes || '',
    });
    setEditOpen(true);
  }

  async function saveEdit() {
    try {
      await updateProject(project!.Rid, editForm as Partial<HMGPProject>);
      notifications.show({ title: 'Saved', message: 'Project updated.', color: 'green' });
      setEditOpen(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to save.', color: 'red' }); }
  }

  // ── Document upload handler ────────────────────────────────
  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !project) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(',')[1];
        if (!base64) { setUploading(false); return; }
        await createDocument({
          projectId: String(project.Rid),
          organizationId: project.organizationId,
          documentName: file.name,
          documentType: file.type.includes('pdf') ? 'PDF' : file.type.includes('image') ? 'Image' : 'Document',
          description: '',
          uploadedBy: user?.email || '',
          uploadDate: new Date().toISOString().split('T')[0],
          tags: uploadDocType,
          notes: base64.length > 60000 ? '' : base64,
          mimeType: file.type,
          fileSize: String(file.size),
        } as Partial<PADocument>);
        const checkItem = checklistItems.find(c => c.key === uploadDocType);
        if (checkItem) {
          await createTimelineEvent({
            projectId: String(project.Rid),
            organizationId: project.organizationId,
            eventType: 'submission',
            eventDate: new Date().toISOString().split('T')[0],
            description: `${checkItem.label} uploaded [checklist:${checkItem.key}]`,
            creatorId: user?.email || '',
            status: 'completed',
          } as Partial<PATimelineEvent>);
        }
        await Promise.all([fetchDocuments(), fetchTimeline()]);
        notifications.show({ title: 'Uploaded', message: `${file.name} uploaded${checkItem ? ` — ${checkItem.label} complete` : ''}`, color: 'green' });
      };
      reader.readAsDataURL(file);
    } catch {
      notifications.show({ title: 'Error', message: 'Upload failed', color: 'red' });
    } finally {
      setUploading(false);
      setUploadDocType('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  // ── Checklist mark-done handler ────────────────────────────
  async function handleCheckOff() {
    if (!checkingItem || !project) return;
    await createTimelineEvent({
      projectId: String(project.Rid),
      organizationId: project.organizationId,
      eventType: 'milestone',
      eventDate: new Date().toISOString().split('T')[0],
      description: `${checkingItem.label} completed [checklist:${checkingItem.key}]${checkNotes ? ' — ' + checkNotes : ''}`,
      creatorId: user?.email || '',
      status: 'completed',
    } as Partial<PATimelineEvent>);
    await fetchTimeline();
    notifications.show({ title: 'Done', message: `${checkingItem.label} marked complete`, color: 'green' });
    setCheckingItem(null);
    setCheckNotes('');
  }

  // ── Render ──────────────────────────────────────────────────
  return (
    <Stack gap="md">
      <Breadcrumbs items={[
        { label: 'Disaster Grants', to: '/pagrants' },
        { label: 'HMGP', to: '/pagrants/hmgp' },
        { label: 'Projects', to: '/pagrants/hmgp/projects' },
        { label: project.projectNumber || 'Project' },
      ]} />

      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Group gap="xs">
              <Title order={3}>{project.projectTitle || 'Untitled Project'}</Title>
              <ActionIcon variant="subtle" color="green" onClick={openEdit}><IconEdit size={18} /></ActionIcon>
            </Group>
            <Group gap="xs">
              {project.projectNumber && <Text size="sm" c="dimmed">{project.projectNumber}</Text>}
              {orgName && <Text size="sm" c="dimmed">| {orgName}</Text>}
            </Group>
          </Stack>
          <Group gap="xs">
            <Badge color={HMGP_PROJECT_TYPE_COLORS[pType] || '#6B7280'} variant="filled" size="lg">
              {HMGP_PROJECT_TYPE_LABELS[pType] || pType}
            </Badge>
            <Badge color={HMGP_PHASE_COLORS[phase] || 'gray'} variant="light" size="lg">
              {HMGP_PHASE_LABELS[phase] || phase}
            </Badge>
            <Badge color="gray" variant="outline" size="lg">
              {HMGP_STATUS_LABELS[project.status as keyof typeof HMGP_STATUS_LABELS] || project.status}
            </Badge>
          </Group>
        </Group>
      </Paper>

      {/* Tabs */}
      <Tabs defaultValue="checklist" color="green">
        <Tabs.List>
          <Tabs.Tab value="checklist" leftSection={<IconListCheck size={16} />}>Checklist ({checklistDone}/{checklistItems.length})</Tabs.Tab>
          <Tabs.Tab value="overview" leftSection={<IconHome size={16} />}>Overview</Tabs.Tab>
          <Tabs.Tab value="bca" leftSection={<IconChartBar size={16} />}>BCA</Tabs.Tab>
          <Tabs.Tab value="ehp" leftSection={<IconLeaf size={16} />}>EHP</Tabs.Tab>
          {isPropertyType && <Tabs.Tab value="property" leftSection={<IconBuildingBank size={16} />}>Property</Tabs.Tab>}
          <Tabs.Tab value="costs" leftSection={<IconCash size={16} />}>Costs ({myCosts.length})</Tabs.Tab>
          <Tabs.Tab value="documents" leftSection={<IconFileText size={16} />}>Documents ({myDocs.length})</Tabs.Tab>
          <Tabs.Tab value="timeline" leftSection={<IconTimeline size={16} />}>Timeline ({myTimeline.length})</Tabs.Tab>
          <Tabs.Tab value="notes" leftSection={<IconNotes size={16} />}>Notes ({myNotes.length})</Tabs.Tab>
          <Tabs.Tab value="photos" leftSection={<IconPhoto size={16} />}>Photos</Tabs.Tab>
          <Tabs.Tab value="tasks" leftSection={<IconSubtask size={16} />}>Tasks</Tabs.Tab>
        </Tabs.List>

        {/* ────── Checklist ────── */}
        <Tabs.Panel value="checklist" pt="md">
          <Stack gap="md">
            {/* Progress summary */}
            <Paper p="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text fw={600}>Completion Progress</Text>
                <Group gap="xs">
                  <Badge color="green" variant="light">{checklistDone} / {checklistItems.length} total</Badge>
                  <Badge color={checklistRequiredDone === checklistRequired ? 'green' : 'orange'} variant="filled">
                    {checklistRequiredDone} / {checklistRequired} required
                  </Badge>
                </Group>
              </Group>
              <Progress
                value={checklistItems.length ? (checklistDone / checklistItems.length) * 100 : 0}
                color={checklistRequiredDone === checklistRequired ? 'green' : 'orange'}
                size="lg" radius="xl"
              />
            </Paper>

            {/* Grouped by category */}
            {(['document', 'form', 'review', 'milestone'] as const).map(cat => {
              const items = checklistItems.filter(c => c.category === cat);
              if (items.length === 0) return null;
              return (
                <Paper key={cat} p="md" withBorder>
                  <Group gap="xs" mb="sm">
                    <Badge color={CHECKLIST_CATEGORY_COLORS[cat]} variant="filled" size="lg">
                      {CHECKLIST_CATEGORY_LABELS[cat]}s
                    </Badge>
                    <Text size="sm" c="dimmed">
                      {items.filter(c => checklistStatus.get(c.key)?.done).length} / {items.length} complete
                    </Text>
                  </Group>
                  <Stack gap="xs">
                    {items.map(item => {
                      const st = checklistStatus.get(item.key);
                      const done = st?.done || false;
                      return (
                        <Paper key={item.key} p="xs" withBorder style={{
                          borderLeft: `3px solid ${done ? '#059669' : '#D1D5DB'}`,
                          opacity: done ? 0.85 : 1,
                        }}>
                          <Group justify="space-between" wrap="nowrap">
                            <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                              {done
                                ? <ThemeIcon color="green" variant="light" radius="xl" size="sm"><IconCheck size={14} /></ThemeIcon>
                                : <ThemeIcon color="gray" variant="light" radius="xl" size="sm"><IconCircle size={14} /></ThemeIcon>
                              }
                              <Stack gap={0} style={{ flex: 1 }}>
                                <Group gap="xs">
                                  <Text size="sm" fw={500} td={done ? 'line-through' : undefined}>{item.label}</Text>
                                  {item.required && <Badge size="xs" color="red" variant="light">Required</Badge>}
                                </Group>
                                <Text size="xs" c="dimmed">{item.description}</Text>
                                {done && st?.date && (
                                  <Text size="xs" c="green.7">Completed {st.date}{st.by ? ` by ${st.by}` : ''}</Text>
                                )}
                              </Stack>
                            </Group>
                            {!done && (
                              item.category === 'document'
                                ? <Button size="xs" variant="light" color="blue" leftSection={<IconUpload size={14} />}
                                    loading={uploading && uploadDocType === item.key}
                                    onClick={() => {
                                      setUploadDocType(item.key);
                                      fileInputRef.current?.click();
                                    }}>Upload</Button>
                                : <Button size="xs" variant="light" color="green" leftSection={<IconCheckbox size={14} />}
                                    onClick={() => { setCheckingItem(item); setCheckNotes(''); }}>Mark Done</Button>
                            )}
                            {done && (
                              <Button size="xs" variant="subtle" color="orange"
                                onClick={async () => {
                                  // Find and delete the timeline event(s) that mark this as done
                                  const matchingEvents = myTimeline.filter(t =>
                                    t.description?.includes(`[checklist:${item.key}]`)
                                  );
                                  for (const evt of matchingEvents) {
                                    await deleteTimelineEvent(evt.Rid);
                                  }
                                  // Find and delete matching documents (by tags)
                                  const matchingDocs = myDocs.filter(d => d.tags === item.key);
                                  for (const doc of matchingDocs) {
                                    await deleteDocument(doc.Rid);
                                  }
                                  await Promise.all([fetchTimeline(), fetchDocuments()]);
                                  notifications.show({ title: 'Undone', message: `${item.label} marked incomplete`, color: 'orange' });
                                }}>
                                Undo
                              </Button>
                            )}
                          </Group>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Tabs.Panel>

        {/* ────── Overview ────── */}
        <Tabs.Panel value="overview" pt="md">
          <OverviewTab project={project} estCost={estCost} fedShare={fedShare}
            applicantMatch={applicantMatch} awarded={awarded} drawn={drawn} fedPct={fedPct} />
        </Tabs.Panel>

        {/* ────── BCA ────── */}
        <Tabs.Panel value="bca" pt="md">
          <BCATab project={project} updateProject={updateProject} />
        </Tabs.Panel>

        {/* ────── EHP ────── */}
        <Tabs.Panel value="ehp" pt="md">
          <EHPTab project={project} updateProject={updateProject} />
        </Tabs.Panel>

        {/* ────── Property ────── */}
        {isPropertyType && (
          <Tabs.Panel value="property" pt="md">
            <PropertyTab project={project} updateProject={updateProject} />
          </Tabs.Panel>
        )}

        {/* ────── Costs (nested sub-tabs) ────── */}
        <Tabs.Panel value="costs" pt="md">
          <Tabs defaultValue="summary" variant="outline" color="green">
            <Tabs.List mb="md">
              <Tabs.Tab value="summary">Summary ({myCosts.length})</Tabs.Tab>
              <Tabs.Tab value="labor">Labor ({myLabor.length})</Tabs.Tab>
              <Tabs.Tab value="equipment">Equipment ({myEquip.length})</Tabs.Tab>
              <Tabs.Tab value="materials">Materials ({myMaterials.length})</Tabs.Tab>
              <Tabs.Tab value="contracts">Contracts ({myContracts.length})</Tabs.Tab>
              <Tabs.Tab value="rented">Rented Equip ({myRented.length})</Tabs.Tab>
              <Tabs.Tab value="donated">Donated ({myDonated.length})</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="summary">
              <CostsTab costs={myCosts} projectId={projectId!}
                createProjectCost={createProjectCost} updateProjectCost={updateProjectCost} deleteProjectCost={deleteProjectCost} />
            </Tabs.Panel>

            <Tabs.Panel value="labor">
              <LaborSubTab records={myLabor} projectId={projectId!} organizationId={project.organizationId}
                createLabor={createLabor} updateLabor={updateLabor} deleteLabor={deleteLabor} />
            </Tabs.Panel>

            <Tabs.Panel value="equipment">
              <EquipmentSubTab records={myEquip} projectId={projectId!} organizationId={project.organizationId}
                createEquip={createEquip} updateEquip={updateEquip} deleteEquip={deleteEquip} />
            </Tabs.Panel>

            <Tabs.Panel value="materials">
              <MaterialsSubTab records={myMaterials} projectId={projectId!} organizationId={project.organizationId}
                createMaterial={createMaterial} updateMaterial={updateMaterial} deleteMaterial={deleteMaterial} />
            </Tabs.Panel>

            <Tabs.Panel value="contracts">
              <ContractsSubTab records={myContracts} projectId={projectId!} organizationId={project.organizationId}
                createContract={createContract} updateContract={updateContract} deleteContract={deleteContract} />
            </Tabs.Panel>

            <Tabs.Panel value="rented">
              <RentedSubTab records={myRented} projectId={projectId!} organizationId={project.organizationId}
                createRented={createRented} updateRented={updateRented} deleteRented={deleteRented} />
            </Tabs.Panel>

            <Tabs.Panel value="donated">
              <DonatedSubTab records={myDonated} projectId={projectId!} organizationId={project.organizationId}
                createDonated={createDonated} updateDonated={updateDonated} deleteDonated={deleteDonated} />
            </Tabs.Panel>
          </Tabs>
        </Tabs.Panel>

        {/* ────── Documents ────── */}
        <Tabs.Panel value="documents" pt="md">
          <DocumentsTab docs={myDocs} projectId={projectId!}
            createDocument={createDocument} deleteDocument={deleteDocument}
            docTypeOptions={DOC_TYPE_OPTIONS} />
        </Tabs.Panel>

        {/* ────── Timeline ────── */}
        <Tabs.Panel value="timeline" pt="md">
          <TimelineTab events={myTimeline} projectId={projectId!}
            createTimelineEvent={createTimelineEvent} deleteTimelineEvent={deleteTimelineEvent} />
        </Tabs.Panel>

        {/* ────── Notes ────── */}
        <Tabs.Panel value="notes" pt="md">
          <NotesTab notes={myNotes} projectId={projectId!}
            createNote={createNote} deleteNote={deleteNote}
            userId={user?.email || 'unknown'} />
        </Tabs.Panel>

        {/* ────── Photos ────── */}
        <Tabs.Panel value="photos" pt="md">
          <PhotosTab docs={myDocs} projectId={projectId!}
            createDocument={createDocument} deleteDocument={deleteDocument} fetchDocuments={fetchDocuments} />
        </Tabs.Panel>

        {/* ────── Tasks ────── */}
        <Tabs.Panel value="tasks" pt="md">
          <TasksTab events={myTimeline} projectId={projectId!}
            createTimelineEvent={createTimelineEvent} deleteTimelineEvent={deleteTimelineEvent}
            userId={user?.email || 'unknown'} organizationId={project.organizationId} />
        </Tabs.Panel>

      </Tabs>

      {/* Hidden file input for document uploads */}
      <input type="file" ref={fileInputRef} style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.xlsx,.xls,.pptx,.png,.jpg,.jpeg,.zip"
        onChange={handleFileUpload} />

      {/* Check-off confirmation modal */}
      <Modal opened={!!checkingItem} onClose={() => setCheckingItem(null)} title={`Complete: ${checkingItem?.label || ''}`} size="sm">
        <Stack gap="sm">
          <Text size="sm">{checkingItem?.description}</Text>
          <Textarea label="Notes (optional)" value={checkNotes} onChange={e => setCheckNotes(e.currentTarget.value)} rows={2} />
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setCheckingItem(null)}>Cancel</Button>
            <Button color="green" onClick={handleCheckOff}>Mark Complete</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal opened={editOpen} onClose={() => setEditOpen(false)} title="Edit HMGP Project" size="lg">
        <Stack gap="sm">
          <TextInput label="Project Title" value={editForm.projectTitle || ''}
            onChange={e => setEditForm(f => ({ ...f, projectTitle: e.currentTarget.value }))} />
          <Group grow>
            <Select label="Project Type" data={PROJECT_TYPE_OPTIONS} value={editForm.projectType || null}
              onChange={v => setEditForm(f => ({ ...f, projectType: v || '' }))} />
            <Select label="Phase" data={PHASE_OPTIONS} value={editForm.phase || null}
              onChange={v => setEditForm(f => ({ ...f, phase: v || '' }))} />
            <Select label="Status" data={STATUS_OPTIONS} value={editForm.status || null}
              onChange={v => setEditForm(f => ({ ...f, status: v || '' }))} />
          </Group>
          <Group grow>
            <TextInput label="Estimated Cost" value={formatCurrency(editForm.estimatedCost || '')}
              onChange={e => setEditForm(f => ({ ...f, estimatedCost: stripCurrency(e.currentTarget.value) }))}
              styles={{ input: { fontFamily: 'monospace' } }} />
            <TextInput label="Federal Share %" value={editForm.federalSharePct || ''}
              onChange={e => setEditForm(f => ({ ...f, federalSharePct: e.currentTarget.value }))} />
            <TextInput label="Award Amount" value={formatCurrency(editForm.awardAmount || '')}
              onChange={e => setEditForm(f => ({ ...f, awardAmount: stripCurrency(e.currentTarget.value) }))}
              styles={{ input: { fontFamily: 'monospace' } }} />
          </Group>
          <Group grow>
            <TextInput label="Address" value={editForm.projectAddress || ''}
              onChange={e => setEditForm(f => ({ ...f, projectAddress: e.currentTarget.value }))} />
            <TextInput label="City" value={editForm.projectCity || ''}
              onChange={e => setEditForm(f => ({ ...f, projectCity: e.currentTarget.value }))} />
            <TextInput label="State" value={editForm.projectState || ''}
              onChange={e => setEditForm(f => ({ ...f, projectState: e.currentTarget.value }))} />
            <TextInput label="ZIP" value={editForm.projectZip || ''}
              onChange={e => setEditForm(f => ({ ...f, projectZip: e.currentTarget.value }))} />
          </Group>
          <Group grow>
            <TextInput label="Performance Period Start" type="date" value={editForm.performancePeriodStart || ''}
              onChange={e => setEditForm(f => ({ ...f, performancePeriodStart: e.currentTarget.value }))} />
            <TextInput label="Performance Period End" type="date" value={editForm.performancePeriodEnd || ''}
              onChange={e => setEditForm(f => ({ ...f, performancePeriodEnd: e.currentTarget.value }))} />
          </Group>
          <TextInput label="Assigned Specialist" value={editForm.assignedSpecialist || ''}
            onChange={e => setEditForm(f => ({ ...f, assignedSpecialist: e.currentTarget.value }))} />
          <Textarea label="Problem Description" autosize minRows={2} value={editForm.problemDescription || ''}
            onChange={e => setEditForm(f => ({ ...f, problemDescription: e.currentTarget.value }))} />
          <Textarea label="Scope of Work" autosize minRows={2} value={editForm.scopeOfWork || ''}
            onChange={e => setEditForm(f => ({ ...f, scopeOfWork: e.currentTarget.value }))} />
          <Textarea label="Notes" autosize minRows={2} value={editForm.notes || ''}
            onChange={e => setEditForm(f => ({ ...f, notes: e.currentTarget.value }))} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button color="green" onClick={saveEdit} leftSection={<IconDeviceFloppy size={16} />}>Save</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}


// ═══════════════════════════════════════════════════════════════
// Tab Components
// ═══════════════════════════════════════════════════════════════

// ────── Overview Tab ──────────────────────────────────────────

function OverviewTab({ project, estCost, fedShare, applicantMatch, awarded, drawn, fedPct }: {
  project: HMGPProject; estCost: number; fedShare: number; applicantMatch: number;
  awarded: number; drawn: number; fedPct: number;
}) {
  return (
    <Stack gap="md">
      {/* Financial Summary */}
      <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }}>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${HMGP_GREEN}` }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Estimated Cost</Text>
          <Text size="xl" fw={700}>{fmt(estCost)}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #0284C7' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Federal Share ({fedPct}%)</Text>
          <Text size="xl" fw={700}>{fmt(fedShare)}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #D97706' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Applicant Match</Text>
          <Text size="xl" fw={700}>{fmt(applicantMatch)}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #7C3AED' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Awarded</Text>
          <Text size="xl" fw={700}>{fmt(awarded)}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #DC2626' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Drawn Down</Text>
          <Text size="xl" fw={700}>{fmt(drawn)}</Text>
          {awarded > 0 && <Text size="xs" c="dimmed">{((drawn / awarded) * 100).toFixed(0)}% of award</Text>}
        </Paper>
      </SimpleGrid>

      {/* Project Details */}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Paper p="md" withBorder>
          <Text fw={600} mb="xs">Project Details</Text>
          <Stack gap={6}>
            <Group gap="xs"><Text size="sm" c="dimmed" w={160}>FEMA App Number:</Text><Text size="sm">{project.femaAppNumber || '--'}</Text></Group>
            <Group gap="xs"><Text size="sm" c="dimmed" w={160}>Subapplication:</Text><Text size="sm">{project.subapplicationId || '--'}</Text></Group>
            <Group gap="xs"><Text size="sm" c="dimmed" w={160}>State Priority Rank:</Text><Text size="sm">{project.statePriorityRank || '--'}</Text></Group>
            <Group gap="xs"><Text size="sm" c="dimmed" w={160}>Assigned Specialist:</Text><Text size="sm">{project.assignedSpecialist || '--'}</Text></Group>
            <Group gap="xs"><Text size="sm" c="dimmed" w={160}>Location:</Text>
              <Text size="sm">{[project.projectAddress, project.projectCity, project.projectState, project.projectZip].filter(Boolean).join(', ') || '--'}</Text>
            </Group>
          </Stack>
        </Paper>

        <Paper p="md" withBorder>
          <Text fw={600} mb="xs">Key Dates</Text>
          <Stack gap={6}>
            <Group gap="xs"><Text size="sm" c="dimmed" w={160}>Subapplication Date:</Text><Text size="sm">{project.subapplicationDate || '--'}</Text></Group>
            <Group gap="xs"><Text size="sm" c="dimmed" w={160}>FEMA Award Date:</Text><Text size="sm">{project.femaAwardDate || '--'}</Text></Group>
            <Group gap="xs"><Text size="sm" c="dimmed" w={160}>Performance Start:</Text><Text size="sm">{project.performancePeriodStart || '--'}</Text></Group>
            <Group gap="xs"><Text size="sm" c="dimmed" w={160}>Performance End:</Text><Text size="sm">{project.performancePeriodEnd || '--'}</Text></Group>
            <Group gap="xs"><Text size="sm" c="dimmed" w={160}>Quarterly Report Due:</Text><Text size="sm">{project.quarterlyReportDue || '--'}</Text></Group>
            <Group gap="xs"><Text size="sm" c="dimmed" w={160}>Created:</Text><Text size="sm">{project.createdDate || '--'}</Text></Group>
          </Stack>
        </Paper>
      </SimpleGrid>

      {/* Problem & Scope */}
      {(project.problemDescription || project.scopeOfWork) && (
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          {project.problemDescription && (
            <Paper p="md" withBorder>
              <Text fw={600} mb="xs">Problem Description</Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{project.problemDescription}</Text>
            </Paper>
          )}
          {project.scopeOfWork && (
            <Paper p="md" withBorder>
              <Text fw={600} mb="xs">Scope of Work</Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{project.scopeOfWork}</Text>
            </Paper>
          )}
        </SimpleGrid>
      )}

      {/* BCA Quick Summary */}
      <Paper p="md" withBorder>
        <Group justify="space-between">
          <Text fw={600}>Benefit-Cost Analysis</Text>
          <Badge color={num(project.bcaRatio) >= 1.0 ? 'green' : num(project.bcaRatio) > 0 ? 'red' : 'gray'} size="lg" variant="light">
            BCR: {project.bcaRatio || 'N/A'}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed" mt={4}>
          {project.bcaMethodology ? BCA_METHODOLOGY_OPTIONS.find(o => o.value === project.bcaMethodology)?.label || project.bcaMethodology : 'No methodology selected'}
          {project.bcaCompletedDate ? ` | Completed ${project.bcaCompletedDate}` : ''}
        </Text>
      </Paper>
    </Stack>
  );
}


// ────── BCA Tab ──────────────────────────────────────────────

function BCATab({ project, updateProject }: {
  project: HMGPProject;
  updateProject: (rid: number, data: Partial<HMGPProject>) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    bcaRequired: project.bcaRequired || '',
    bcaMethodology: project.bcaMethodology || '',
    bcaRatio: project.bcaRatio || '',
    pvBenefits: project.pvBenefits || '',
    pvCosts: project.pvCosts || '',
    usefulLife: project.usefulLife || '',
    discountRate: project.discountRate || '',
    bcaToolVersion: project.bcaToolVersion || '',
    bcaCompletedDate: project.bcaCompletedDate || '',
  });

  const bcr = num(form.bcaRatio);
  const costEffective = bcr >= 1.0;
  const hasBCR = bcr > 0;

  async function save() {
    try {
      await updateProject(project.Rid, form as Partial<HMGPProject>);
      notifications.show({ title: 'Saved', message: 'BCA data updated.', color: 'green' });
      setEditing(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to save BCA.', color: 'red' }); }
  }

  return (
    <Stack gap="md">
      {/* Cost-Effectiveness Determination Banner */}
      {hasBCR && (
        <Paper p="md" withBorder style={{
          borderLeft: `4px solid ${costEffective ? '#059669' : '#DC2626'}`,
          background: costEffective ? '#ECFDF5' : '#FEF2F2',
        }}>
          <Group gap="sm">
            <ThemeIcon size="lg" color={costEffective ? 'green' : 'red'} variant="light" radius="xl">
              {costEffective ? <IconCheck size={20} /> : <IconX size={20} />}
            </ThemeIcon>
            <Stack gap={0}>
              <Text fw={700} size="lg" c={costEffective ? 'green.8' : 'red.8'}>
                {costEffective ? 'Cost-Effective' : 'Not Cost-Effective'}
              </Text>
              <Text size="sm" c="dimmed">
                BCR of {bcr.toFixed(2)} {costEffective ? 'meets' : 'does not meet'} the minimum 1.0 threshold required for HMGP funding.
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}

      <Group justify="space-between">
        <Text fw={600} size="lg">Benefit-Cost Analysis</Text>
        {!editing
          ? <Button variant="light" color="green" leftSection={<IconEdit size={16} />} onClick={() => setEditing(true)}>Edit BCA</Button>
          : <Group gap="xs">
              <Button variant="default" onClick={() => setEditing(false)}>Cancel</Button>
              <Button color="green" leftSection={<IconDeviceFloppy size={16} />} onClick={save}>Save</Button>
            </Group>
        }
      </Group>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Paper p="md" withBorder>
          <Text fw={600} mb="sm">Analysis Parameters</Text>
          <Stack gap="sm">
            <Select label="BCA Required" data={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
              value={form.bcaRequired || null} disabled={!editing}
              onChange={v => setForm(f => ({ ...f, bcaRequired: v || '' }))} />
            <Select label="Methodology" data={BCA_METHODOLOGY_OPTIONS}
              value={form.bcaMethodology || null} disabled={!editing}
              onChange={v => setForm(f => ({ ...f, bcaMethodology: v || '' }))} />
            <TextInput label="Useful Life (years)" value={form.usefulLife} disabled={!editing}
              onChange={e => setForm(f => ({ ...f, usefulLife: e.currentTarget.value }))} />
            <TextInput label="Discount Rate" value={form.discountRate} disabled={!editing}
              onChange={e => setForm(f => ({ ...f, discountRate: e.currentTarget.value }))} />
            <TextInput label="BCA Tool Version" value={form.bcaToolVersion} disabled={!editing}
              onChange={e => setForm(f => ({ ...f, bcaToolVersion: e.currentTarget.value }))} />
            <TextInput label="BCA Completed Date" type="date" value={form.bcaCompletedDate} disabled={!editing}
              onChange={e => setForm(f => ({ ...f, bcaCompletedDate: e.currentTarget.value }))} />
          </Stack>
        </Paper>

        <Paper p="md" withBorder>
          <Text fw={600} mb="sm">Results</Text>
          <Stack gap="sm">
            <Paper p="lg" withBorder ta="center" style={{
              borderTop: `3px solid ${hasBCR ? (costEffective ? '#059669' : '#DC2626') : '#9CA3AF'}`,
            }}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>BCR Ratio</Text>
              <Text size="xl" fw={700} c={hasBCR ? (costEffective ? 'green.7' : 'red.7') : 'gray'}>
                {hasBCR ? bcr.toFixed(2) : '--'}
              </Text>
            </Paper>
            <TextInput label="BCR Ratio" value={form.bcaRatio} disabled={!editing}
              description="Must be >= 1.0 for HMGP eligibility"
              onChange={e => setForm(f => ({ ...f, bcaRatio: e.currentTarget.value }))} />
            <TextInput label="Present Value of Benefits" value={form.pvBenefits} disabled={!editing}
              onChange={e => setForm(f => ({ ...f, pvBenefits: e.currentTarget.value }))} />
            <TextInput label="Present Value of Costs" value={form.pvCosts} disabled={!editing}
              onChange={e => setForm(f => ({ ...f, pvCosts: e.currentTarget.value }))} />
            {hasBCR && (
              <Group grow>
                <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #0284C7' }}>
                  <Text size="xs" c="dimmed">PV Benefits</Text>
                  <Text fw={600}>{fmt(num(form.pvBenefits))}</Text>
                </Paper>
                <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #D97706' }}>
                  <Text size="xs" c="dimmed">PV Costs</Text>
                  <Text fw={600}>{fmt(num(form.pvCosts))}</Text>
                </Paper>
              </Group>
            )}
          </Stack>
        </Paper>
      </SimpleGrid>
    </Stack>
  );
}


// ────── EHP Tab ─────────────────────────────────────────────

function EHPTab({ project, updateProject }: {
  project: HMGPProject;
  updateProject: (rid: number, data: Partial<HMGPProject>) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    ehpStatus: project.ehpStatus || '',
    ehpCompletedDate: project.ehpCompletedDate || '',
    nepaLevel: project.nepaLevel || '',
    nepaStatus: project.nepaStatus || '',
    section106Required: project.section106Required || '',
    section106Status: project.section106Status || '',
    floodZone: project.floodZone || '',
  });

  // Since HMGPProject doesn't have all compliance fields directly, we track a subset
  // that's on the project record and show status icons
  const items = [
    { key: 'nepa', label: 'NEPA Review', status: form.nepaStatus },
    { key: 'section106', label: 'Section 106 (Historic Preservation)', status: form.section106Status },
    { key: 'ehp', label: 'Overall EHP Review', status: form.ehpStatus },
  ];

  function statusIcon(status: string) {
    if (status === 'completed') return <ThemeIcon color="green" variant="light" radius="xl" size="sm"><IconCheck size={14} /></ThemeIcon>;
    if (status === 'in_progress') return <ThemeIcon color="yellow" variant="light" radius="xl" size="sm"><IconClock size={14} /></ThemeIcon>;
    if (status === 'not_required') return <ThemeIcon color="gray" variant="light" radius="xl" size="sm"><IconMinus size={14} /></ThemeIcon>;
    return <ThemeIcon color="gray" variant="light" radius="xl" size="sm"><IconClock size={14} /></ThemeIcon>;
  }

  async function save() {
    try {
      await updateProject(project.Rid, form as Partial<HMGPProject>);
      notifications.show({ title: 'Saved', message: 'EHP data updated.', color: 'green' });
      setEditing(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to save EHP.', color: 'red' }); }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600} size="lg">Environmental and Historic Preservation (EHP)</Text>
        {!editing
          ? <Button variant="light" color="green" leftSection={<IconEdit size={16} />} onClick={() => setEditing(true)}>Edit EHP</Button>
          : <Group gap="xs">
              <Button variant="default" onClick={() => setEditing(false)}>Cancel</Button>
              <Button color="green" leftSection={<IconDeviceFloppy size={16} />} onClick={save}>Save</Button>
            </Group>
        }
      </Group>

      {/* Status Summary */}
      <Paper p="md" withBorder>
        <Text fw={600} mb="sm">Compliance Checklist</Text>
        <Stack gap="xs">
          {items.map(item => (
            <Group key={item.key} gap="sm">
              {statusIcon(item.status)}
              <Text size="sm" fw={500}>{item.label}</Text>
              <Badge size="sm" color={
                item.status === 'completed' ? 'green' : item.status === 'in_progress' ? 'yellow' :
                item.status === 'not_required' ? 'gray' : 'gray'
              } variant="light">
                {EHP_STATUS_OPTIONS.find(o => o.value === item.status)?.label || item.status || 'Not Started'}
              </Badge>
            </Group>
          ))}
        </Stack>
      </Paper>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* NEPA */}
        <Paper p="md" withBorder>
          <Group gap="xs" mb="sm">
            <IconLeaf size={18} color={HMGP_GREEN} />
            <Text fw={600}>NEPA</Text>
          </Group>
          <Stack gap="sm">
            <Select label="NEPA Level" data={NEPA_LEVEL_OPTIONS}
              value={form.nepaLevel || null} disabled={!editing}
              onChange={v => setForm(f => ({ ...f, nepaLevel: v || '' }))} />
            <Select label="NEPA Status" data={EHP_STATUS_OPTIONS}
              value={form.nepaStatus || null} disabled={!editing}
              onChange={v => setForm(f => ({ ...f, nepaStatus: v || '' }))} />
          </Stack>
        </Paper>

        {/* Section 106 */}
        <Paper p="md" withBorder>
          <Group gap="xs" mb="sm">
            <IconBuildingBank size={18} color={HMGP_GREEN} />
            <Text fw={600}>Section 106 Historic Preservation</Text>
          </Group>
          <Stack gap="sm">
            <Select label="Section 106 Required" data={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
              value={form.section106Required || null} disabled={!editing}
              onChange={v => setForm(f => ({ ...f, section106Required: v || '' }))} />
            <Select label="Section 106 Status" data={EHP_STATUS_OPTIONS}
              value={form.section106Status || null} disabled={!editing}
              onChange={v => setForm(f => ({ ...f, section106Status: v || '' }))} />
          </Stack>
        </Paper>

        {/* Floodplain */}
        <Paper p="md" withBorder>
          <Group gap="xs" mb="sm">
            <IconDroplet size={18} color="#0284C7" />
            <Text fw={600}>Floodplain</Text>
          </Group>
          <Stack gap="sm">
            <TextInput label="Flood Zone" value={form.floodZone} disabled={!editing}
              onChange={e => setForm(f => ({ ...f, floodZone: e.currentTarget.value }))} />
          </Stack>
        </Paper>

        {/* Overall EHP */}
        <Paper p="md" withBorder>
          <Group gap="xs" mb="sm">
            <IconShieldCheck size={18} color={HMGP_GREEN} />
            <Text fw={600}>Overall EHP Status</Text>
          </Group>
          <Stack gap="sm">
            <Select label="EHP Status" data={EHP_STATUS_OPTIONS}
              value={form.ehpStatus || null} disabled={!editing}
              onChange={v => setForm(f => ({ ...f, ehpStatus: v || '' }))} />
            <TextInput label="EHP Completed Date" type="date" value={form.ehpCompletedDate} disabled={!editing}
              onChange={e => setForm(f => ({ ...f, ehpCompletedDate: e.currentTarget.value }))} />
          </Stack>
        </Paper>
      </SimpleGrid>
    </Stack>
  );
}


// ────── Property Tab ─────────────────────────────────────────

function PropertyTab({ project, updateProject }: {
  project: HMGPProject;
  updateProject: (rid: number, data: Partial<HMGPProject>) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    numberOfStructures: project.numberOfStructures || '',
    structureType: project.structureType || '',
    preDisasterValue: project.preDisasterValue || '',
    floodZone: project.floodZone || '',
    repetitiveLoss: project.repetitiveLoss || '',
    severeRepetitiveLoss: project.severeRepetitiveLoss || '',
    // NOTE: nfipClaimCount and nfipClaimTotal are UI-only for now — no dedicated Ignatius columns yet.
    // To persist, add TEXT fields to the HMGP projects table and register keys in api.ts.
    nfipClaimCount: '',
    nfipClaimTotal: '',
  });

  async function save() {
    try {
      await updateProject(project.Rid, form as Partial<HMGPProject>);
      notifications.show({ title: 'Saved', message: 'Property data updated.', color: 'green' });
      setEditing(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to save property data.', color: 'red' }); }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600} size="lg">Property Information</Text>
        {!editing
          ? <Button variant="light" color="green" leftSection={<IconEdit size={16} />} onClick={() => setEditing(true)}>Edit Property</Button>
          : <Group gap="xs">
              <Button variant="default" onClick={() => setEditing(false)}>Cancel</Button>
              <Button color="green" leftSection={<IconDeviceFloppy size={16} />} onClick={save}>Save</Button>
            </Group>
        }
      </Group>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Paper p="md" withBorder>
          <Text fw={600} mb="sm">Structure Details</Text>
          <Stack gap="sm">
            <TextInput label="Number of Structures" value={form.numberOfStructures} disabled={!editing}
              onChange={e => setForm(f => ({ ...f, numberOfStructures: e.currentTarget.value }))} />
            <Select label="Structure Type" disabled={!editing}
              data={[
                { value: 'single_family', label: 'Single-Family Residential' },
                { value: 'multi_family', label: 'Multi-Family Residential' },
                { value: 'manufactured', label: 'Manufactured / Mobile Home' },
                { value: 'commercial', label: 'Commercial' },
                { value: 'mixed_use', label: 'Mixed Use' },
                { value: 'public_facility', label: 'Public Facility' },
                { value: 'industrial', label: 'Industrial' },
                { value: 'agricultural', label: 'Agricultural' },
              ]}
              value={form.structureType || null}
              onChange={v => setForm(f => ({ ...f, structureType: v || '' }))} />
            <TextInput label="Pre-Disaster Fair Market Value" disabled={!editing}
              value={formatCurrency(form.preDisasterValue)}
              onChange={e => setForm(f => ({ ...f, preDisasterValue: stripCurrency(e.currentTarget.value) }))}
              styles={{ input: { fontFamily: 'monospace' } }} />
            <TextInput label="Number of NFIP Claims" disabled={!editing}
              value={form.nfipClaimCount || ''}
              onChange={e => setForm(f => ({ ...f, nfipClaimCount: e.currentTarget.value }))} />
            <TextInput label="Cumulative NFIP Claim Amount" disabled={!editing}
              value={formatCurrency(form.nfipClaimTotal || '')}
              onChange={e => setForm(f => ({ ...f, nfipClaimTotal: stripCurrency(e.currentTarget.value) }))}
              styles={{ input: { fontFamily: 'monospace' } }} />
          </Stack>
        </Paper>

        <Paper p="md" withBorder>
          <Text fw={600} mb="sm">Flood Risk</Text>
          <Stack gap="sm">
            <Select label="Flood Zone" disabled={!editing}
              data={[
                { value: 'A', label: 'A — 100-Year Floodplain (no BFE)' },
                { value: 'AE', label: 'AE — 100-Year Floodplain (with BFE)' },
                { value: 'AH', label: 'AH — 100-Year Shallow Flooding' },
                { value: 'AO', label: 'AO — 100-Year Sheet Flow' },
                { value: 'AR', label: 'AR — Flood Risk Due to Levee' },
                { value: 'V', label: 'V — Coastal High Hazard (no BFE)' },
                { value: 'VE', label: 'VE — Coastal High Hazard (with BFE)' },
                { value: 'X', label: 'X — Moderate/Minimal Flood Risk' },
                { value: 'X500', label: 'X (Shaded) — 500-Year Floodplain' },
                { value: 'D', label: 'D — Undetermined Flood Hazard' },
              ]}
              value={form.floodZone || null}
              onChange={v => setForm(f => ({ ...f, floodZone: v || '' }))}
              searchable />
            <Select label="Repetitive Loss Property" disabled={!editing}
              data={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
              value={form.repetitiveLoss || null}
              onChange={v => setForm(f => ({ ...f, repetitiveLoss: v || '' }))} />
            <Select label="Severe Repetitive Loss Property" disabled={!editing}
              data={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
              value={form.severeRepetitiveLoss || null}
              onChange={v => setForm(f => ({ ...f, severeRepetitiveLoss: v || '' }))} />
          </Stack>
          {form.repetitiveLoss === 'yes' && (
            <Paper p="xs" mt="sm" bg="orange.0" style={{ borderLeft: '3px solid #D97706' }}>
              <Text size="xs" c="orange.8">Repetitive loss properties receive priority consideration under HMGP.</Text>
            </Paper>
          )}
          {form.severeRepetitiveLoss === 'yes' && (
            <Paper p="xs" mt="sm" bg="red.0" style={{ borderLeft: '3px solid #DC2626' }}>
              <Text size="xs" c="red.8">Severe repetitive loss properties are highest priority for acquisition/elevation.</Text>
            </Paper>
          )}
        </Paper>
      </SimpleGrid>
    </Stack>
  );
}


// ────── Costs Tab ────────────────────────────────────────────

const COST_TYPE_COLORS: Record<string, string> = {
  construction: 'blue', engineering: 'violet', environmental: 'teal',
  permitting: 'gray', property_acquisition: 'cyan', demolition: 'orange',
  relocation: 'pink', project_management: 'indigo', inspection: 'lime',
  equipment: 'yellow', materials: 'grape', labor: 'green',
  contingency: 'red', other: 'gray',
};

const EST_ACTUAL_OPTIONS = [
  { value: 'estimated', label: 'Estimated' },
  { value: 'actual', label: 'Actual' },
];

const emptyCostForm = () => ({
  costType: '', description: '', quantity: '', unit: '', unitCost: '', totalCost: '',
  estimatedOrActual: 'estimated', eligible: 'true', vendorEmployee: '', notes: '',
});

function CostsTab({ costs, projectId, createProjectCost, updateProjectCost, deleteProjectCost }: {
  costs: PAProjectCost[]; projectId: string;
  createProjectCost: (data: Partial<PAProjectCost>) => Promise<number>;
  updateProjectCost: (rid: number, data: Partial<PAProjectCost>) => Promise<void>;
  deleteProjectCost: (rid: number) => Promise<void>;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<PAProjectCost | null>(null);
  const [costForm, setCostForm] = useState(emptyCostForm());
  const [costFilter, setCostFilter] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const total = costs.reduce((s, c) => s + num(c.totalCost), 0);
  const filteredCosts = costFilter ? costs.filter(c => c.costType === costFilter) : costs;

  // Summary by type
  const costsByType = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of costs) map[c.costType] = (map[c.costType] || 0) + num(c.totalCost);
    return map;
  }, [costs]);

  function openAdd() {
    setEditingCost(null);
    setCostForm(emptyCostForm());
    setModalOpen(true);
  }

  function openEdit(c: PAProjectCost) {
    setEditingCost(c);
    setCostForm({
      costType: c.costType, description: c.description,
      quantity: c.quantity, unit: c.unit,
      unitCost: c.unitCost, totalCost: c.totalCost,
      estimatedOrActual: c.estimatedOrActual || 'estimated',
      eligible: c.eligible || 'true',
      vendorEmployee: c.linkedRecordId || '',
      notes: c.notes,
    });
    setModalOpen(true);
  }

  async function handleSave() {
    if (!costForm.costType) { notifications.show({ message: 'Cost type is required', color: 'yellow' }); return; }
    setSaving(true);
    try {
      const data: Partial<PAProjectCost> = {
        projectId,
        costType: costForm.costType,
        description: costForm.description,
        quantity: costForm.quantity,
        unit: costForm.unit,
        unitCost: costForm.unitCost,
        totalCost: costForm.totalCost,
        estimatedOrActual: costForm.estimatedOrActual,
        eligible: costForm.eligible,
        linkedRecordId: costForm.vendorEmployee,
        notes: costForm.notes,
      };
      if (editingCost) {
        await updateProjectCost(editingCost.Rid, data);
        notifications.show({ message: 'Cost line updated', color: 'green' });
      } else {
        await createProjectCost(data);
        notifications.show({ message: 'Cost line added', color: 'green' });
      }
      setModalOpen(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to save cost.', color: 'red' }); }
    finally { setSaving(false); }
  }

  async function removeCost(rid: number) {
    try {
      await deleteProjectCost(rid);
      notifications.show({ message: 'Cost removed.', color: 'green' });
    } catch { notifications.show({ title: 'Error', message: 'Failed to delete.', color: 'red' }); }
  }

  // Auto-calc helper for form
  function updateQty(raw: string) {
    setCostForm(prev => {
      const qty = parseFloat(raw) || 0;
      const uc = parseFloat(prev.unitCost) || 0;
      return { ...prev, quantity: raw, totalCost: qty && uc ? (qty * uc).toFixed(2) : prev.totalCost };
    });
  }

  function updateUnitCost(raw: string) {
    setCostForm(prev => {
      const qty = parseFloat(prev.quantity) || 0;
      const uc = parseFloat(raw) || 0;
      return { ...prev, unitCost: raw, totalCost: qty && uc ? (qty * uc).toFixed(2) : prev.totalCost };
    });
  }

  // Active cost types for filter buttons
  const activeTypes = useMemo(() =>
    [...new Set(costs.map(c => c.costType))].filter(Boolean).sort(),
    [costs]
  );

  return (
    <Stack gap="md">
      {/* ── Cost summary cards ────────────────────────────────── */}
      {activeTypes.length > 0 && (
        <SimpleGrid cols={{ base: 2, sm: 3, md: Math.min(activeTypes.length, 6) }}>
          {activeTypes.map(t => (
            <Paper key={t} p="sm" withBorder style={{ cursor: 'pointer', borderTop: `3px solid ${COST_TYPE_COLORS[t] === 'green' ? '#059669' : `var(--mantine-color-${COST_TYPE_COLORS[t] || 'gray'}-6)`}` }}
              onClick={() => setCostFilter(costFilter === t ? null : t)}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                {COST_TYPE_OPTIONS.find(o => o.value === t)?.label || t}
              </Text>
              <Text size="lg" fw={700}>{fmt(costsByType[t] || 0)}</Text>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      <Group justify="space-between">
        <Group gap="sm">
          <Text fw={600} size="lg">Cost Line Items</Text>
          <Badge color="green" size="lg" variant="light">{fmt(total)}</Badge>
        </Group>
        <Button color="green" leftSection={<IconPlus size={16} />} onClick={openAdd}>Add Cost</Button>
      </Group>

      {/* ── Filter buttons ────────────────────────────────────── */}
      {activeTypes.length > 1 && (
        <Group gap="xs">
          <Button size="xs" variant={costFilter === null ? 'filled' : 'light'} color="green"
            onClick={() => setCostFilter(null)}>All ({costs.length})</Button>
          {activeTypes.map(t => (
            <Button key={t} size="xs"
              variant={costFilter === t ? 'filled' : 'light'}
              color={COST_TYPE_COLORS[t] || 'gray'}
              onClick={() => setCostFilter(costFilter === t ? null : t)}>
              {COST_TYPE_OPTIONS.find(o => o.value === t)?.label || t} ({costs.filter(c => c.costType === t).length})
            </Button>
          ))}
        </Group>
      )}

      {/* ── Costs table ───────────────────────────────────────── */}
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Type</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Vendor/Employee</Table.Th>
            <Table.Th ta="right">Qty</Table.Th>
            <Table.Th>Unit</Table.Th>
            <Table.Th ta="right">Unit Cost</Table.Th>
            <Table.Th ta="right">Total</Table.Th>
            <Table.Th>Est/Act</Table.Th>
            <Table.Th w={70}></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filteredCosts.length === 0 && (
            <Table.Tr><Table.Td colSpan={9}><Text ta="center" c="dimmed" py="md">
              {costFilter ? `No ${COST_TYPE_OPTIONS.find(o => o.value === costFilter)?.label || costFilter} items.` : 'No cost items yet.'}
            </Text></Table.Td></Table.Tr>
          )}
          {filteredCosts.map(c => (
            <Table.Tr key={c.Rid} style={{ cursor: 'pointer' }} onClick={() => openEdit(c)}>
              <Table.Td>
                <Badge size="sm" variant="light" color={COST_TYPE_COLORS[c.costType] || 'gray'}>
                  {COST_TYPE_OPTIONS.find(o => o.value === c.costType)?.label || c.costType}
                </Badge>
              </Table.Td>
              <Table.Td>{c.description || '--'}</Table.Td>
              <Table.Td><Text size="sm" c="dimmed">{c.linkedRecordId || '--'}</Text></Table.Td>
              <Table.Td ta="right">{c.quantity || '--'}</Table.Td>
              <Table.Td><Text size="sm" c="dimmed">{c.unit || '--'}</Text></Table.Td>
              <Table.Td ta="right">{c.unitCost ? fmt(num(c.unitCost)) : '--'}</Table.Td>
              <Table.Td ta="right" fw={600}>{fmt(num(c.totalCost))}</Table.Td>
              <Table.Td>
                <Badge variant="light" size="sm" color={c.estimatedOrActual === 'actual' ? 'green' : 'gray'}>
                  {c.estimatedOrActual === 'actual' ? 'Actual' : 'Est'}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap">
                  <ActionIcon variant="subtle" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(c); }}>
                    <IconEdit size={14} />
                  </ActionIcon>
                  <ActionIcon variant="subtle" size="sm" color="red" onClick={(e) => { e.stopPropagation(); removeCost(c.Rid); }}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {/* ── Add/Edit Cost Modal ───────────────────────────────── */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editingCost ? 'Edit Cost Line Item' : 'Add Cost Line Item'} size="lg">
        <Stack gap="sm">
          <SimpleGrid cols={2}>
            <Select label="Cost Type" required data={COST_TYPE_OPTIONS}
              description="Category of this cost line item"
              value={costForm.costType || null}
              onChange={v => setCostForm(f => ({ ...f, costType: v || '' }))} />
            <Select label="Estimated or Actual" data={EST_ACTUAL_OPTIONS}
              description="Whether this is a projected estimate or verified actual cost"
              value={costForm.estimatedOrActual}
              onChange={v => setCostForm(f => ({ ...f, estimatedOrActual: v || 'estimated' }))} />
          </SimpleGrid>
          <TextInput label="Description" required
            description="Brief description of the work, item, or service"
            value={costForm.description}
            onChange={e => setCostForm(f => ({ ...f, description: e.currentTarget.value }))} />
          <SimpleGrid cols={3}>
            <TextInput label="Quantity"
              description="Number of units (e.g., hours, cubic yards, each)"
              value={costForm.quantity ? Number(costForm.quantity.replace(/,/g, '')).toLocaleString() : ''}
              onChange={e => updateQty(e.currentTarget.value.replace(/,/g, ''))} />
            <TextInput label="Unit"
              description="Unit of measure (e.g., hours, CY, LF, EA, LS)"
              value={costForm.unit}
              onChange={e => setCostForm(f => ({ ...f, unit: e.currentTarget.value }))} />
            <TextInput label="Unit Cost"
              description="Cost per unit"
              value={formatCurrency(costForm.unitCost)}
              onChange={e => updateUnitCost(stripCurrency(e.currentTarget.value))}
              styles={{ input: { fontFamily: 'monospace' } }} />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <TextInput label="Total Cost"
              description="Auto-calculated (Qty x Unit Cost) — editable for overrides"
              value={formatCurrency(costForm.totalCost)}
              onChange={e => setCostForm(f => ({ ...f, totalCost: stripCurrency(e.currentTarget.value) }))}
              styles={{ input: { fontFamily: 'monospace', fontWeight: 600 } }} />
            <Select label="Eligible" data={[
              { value: 'true', label: 'Eligible' },
              { value: 'false', label: 'Ineligible' },
            ]} value={costForm.eligible}
              onChange={v => setCostForm(f => ({ ...f, eligible: v || 'true' }))} />
          </SimpleGrid>
          <TextInput label="Vendor / Employee"
            description="Company name, contractor, or employee associated with this cost"
            value={costForm.vendorEmployee}
            onChange={e => setCostForm(f => ({ ...f, vendorEmployee: e.currentTarget.value }))} />
          <Textarea label="Notes" value={costForm.notes}
            onChange={e => setCostForm(f => ({ ...f, notes: e.currentTarget.value }))} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button color="green" loading={saving} onClick={handleSave}>
              {editingCost ? 'Save Changes' : 'Add Cost'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}


// ────── Documents Tab ────────────────────────────────────────

function DocumentsTab({ docs, projectId, createDocument, deleteDocument, docTypeOptions }: {
  docs: PADocument[]; projectId: string;
  createDocument: (data: Partial<PADocument>) => Promise<number>;
  deleteDocument: (rid: number) => Promise<void>;
  docTypeOptions: Array<{ value: string; label: string }>;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ documentName: '', documentType: '', description: '' });

  async function addDoc() {
    if (!form.documentName) { notifications.show({ title: 'Required', message: 'Enter a document name.', color: 'orange' }); return; }
    try {
      await createDocument({
        projectId,
        documentName: form.documentName,
        documentType: form.documentType,
        description: form.description,
        uploadDate: new Date().toISOString().slice(0, 10),
      } as Partial<PADocument>);
      notifications.show({ title: 'Added', message: 'Document added.', color: 'green' });
      setForm({ documentName: '', documentType: '', description: '' });
      setShowAdd(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to add document.', color: 'red' }); }
  }

  async function removeDoc(rid: number) {
    try {
      await deleteDocument(rid);
      notifications.show({ title: 'Deleted', message: 'Document removed.', color: 'green' });
    } catch { notifications.show({ title: 'Error', message: 'Failed to delete.', color: 'red' }); }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600} size="lg">Documents</Text>
        <Button color="green" leftSection={<IconPlus size={16} />} onClick={() => setShowAdd(true)}>Add Document</Button>
      </Group>

      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Upload Date</Table.Th>
            <Table.Th w={50}></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {docs.length === 0 && (
            <Table.Tr><Table.Td colSpan={5}><Text ta="center" c="dimmed" py="md">No documents yet.</Text></Table.Td></Table.Tr>
          )}
          {docs.map(d => (
            <Table.Tr key={d.Rid}>
              <Table.Td fw={500}>{d.documentName}</Table.Td>
              <Table.Td><Badge size="sm" variant="light">{docTypeOptions.find(o => o.value === d.documentType)?.label || d.documentType}</Badge></Table.Td>
              <Table.Td><Text size="sm" lineClamp={1}>{d.description || '--'}</Text></Table.Td>
              <Table.Td>{d.uploadDate || '--'}</Table.Td>
              <Table.Td>
                <ActionIcon variant="subtle" color="red" size="sm" onClick={() => removeDoc(d.Rid)}>
                  <IconTrash size={14} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={showAdd} onClose={() => setShowAdd(false)} title="Add Document">
        <Stack gap="sm">
          <TextInput label="Document Name" required value={form.documentName}
            onChange={e => setForm(f => ({ ...f, documentName: e.currentTarget.value }))} />
          <Select label="Document Type" data={docTypeOptions} value={form.documentType || null}
            onChange={v => setForm(f => ({ ...f, documentType: v || '' }))} />
          <Textarea label="Description" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.currentTarget.value }))} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button color="green" onClick={addDoc}>Add</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}


// ────── Timeline Tab ─────────────────────────────────────────

function TimelineTab({ events, projectId, createTimelineEvent, deleteTimelineEvent }: {
  events: PATimelineEvent[]; projectId: string;
  createTimelineEvent: (data: Partial<PATimelineEvent>) => Promise<number>;
  deleteTimelineEvent: (rid: number) => Promise<void>;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ eventType: '', eventDate: '', description: '', dueDate: '', status: '' });

  async function addEvent() {
    if (!form.description) { notifications.show({ title: 'Required', message: 'Enter a description.', color: 'orange' }); return; }
    try {
      await createTimelineEvent({
        projectId,
        eventType: form.eventType,
        eventDate: form.eventDate || new Date().toISOString().slice(0, 10),
        description: form.description,
        dueDate: form.dueDate,
        status: form.status || 'pending',
      } as Partial<PATimelineEvent>);
      notifications.show({ title: 'Added', message: 'Timeline event added.', color: 'green' });
      setForm({ eventType: '', eventDate: '', description: '', dueDate: '', status: '' });
      setShowAdd(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to add event.', color: 'red' }); }
  }

  async function removeEvent(rid: number) {
    try {
      await deleteTimelineEvent(rid);
      notifications.show({ title: 'Deleted', message: 'Event removed.', color: 'green' });
    } catch { notifications.show({ title: 'Error', message: 'Failed to delete.', color: 'red' }); }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600} size="lg">Timeline</Text>
        <Button color="green" leftSection={<IconPlus size={16} />} onClick={() => setShowAdd(true)}>Add Event</Button>
      </Group>

      {events.length === 0 && (
        <Paper p="xl" withBorder ta="center">
          <Text c="dimmed">No timeline events yet.</Text>
        </Paper>
      )}

      {/* Horizontal visual timeline */}
      {events.length >= 2 && (
        <Paper p="md" withBorder>
          <Text size="sm" fw={600} mb="sm">Project Timeline</Text>
          <div style={{ position: 'relative', padding: '8px 0', minHeight: 60 }}>
            {/* Horizontal axis line */}
            <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: 2, background: 'var(--mantine-color-gray-3)' }} />
            {/* Today marker */}
            {(() => {
              const effectiveDate = (e: PATimelineEvent) => (e.status === 'completed' && e.completedDate) ? e.completedDate : e.eventDate;
              const dates = events.map(e => effectiveDate(e)).filter(Boolean).sort();
              if (dates.length < 2) return null;
              const minD = new Date(dates[0]).getTime();
              const maxD = new Date(dates[dates.length - 1]).getTime();
              const range = maxD - minD || 1;
              const nowPct = Math.min(100, Math.max(0, ((Date.now() - minD) / range) * 100));
              return (
                <>
                  <div style={{ position: 'absolute', left: `${nowPct}%`, top: 0, bottom: 0, width: 2, background: 'var(--mantine-color-red-5)', zIndex: 2 }} />
                  <Text size="xs" c="red" style={{ position: 'absolute', left: `${nowPct}%`, top: -16, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: 10 }}>Today</Text>
                </>
              );
            })()}
            {/* Event dots */}
            {(() => {
              const effectiveDate = (e: PATimelineEvent) => (e.status === 'completed' && e.completedDate) ? e.completedDate : e.eventDate;
              const dates = events.map(e => effectiveDate(e)).filter(Boolean).sort();
              if (dates.length < 2) return null;
              const minD = new Date(dates[0]).getTime();
              const maxD = new Date(dates[dates.length - 1]).getTime();
              const range = maxD - minD || 1;
              return events.map(evt => {
                const eDate = effectiveDate(evt);
                if (!eDate) return null;
                const pct = ((new Date(eDate).getTime() - minD) / range) * 100;
                const color = evt.status === 'completed' ? HMGP_GREEN :
                  evt.eventType === 'deadline' ? 'var(--mantine-color-red-6)' :
                  'var(--mantine-color-gray-5)';
                const wasLate = evt.status === 'completed' && evt.completedDate && evt.dueDate && evt.completedDate > evt.dueDate;
                return (
                  <Tooltip key={evt.Rid} multiline w={240}
                    label={<div><Text size="xs" fw={600}>{(evt.eventType || 'event').replace(/_/g, ' ')}</Text>{evt.dueDate && <Text size="xs">Due: {evt.dueDate}</Text>}{evt.completedDate && <Text size="xs" c="green">Completed: {evt.completedDate}</Text>}{wasLate && <Badge size="xs" color="orange" variant="light">Completed late</Badge>}<Text size="xs" c="dimmed">{(evt.description || '').substring(0, 120)}</Text></div>}
                    position="top" withArrow>
                    <div style={{
                      position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%, -50%)',
                      width: 14, height: 14, borderRadius: '50%', background: color,
                      border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      cursor: 'pointer', zIndex: 1,
                    }} />
                  </Tooltip>
                );
              });
            })()}
          </div>
          {/* Date range labels */}
          {(() => {
            const dates = events.map(e => (e.status === 'completed' && e.completedDate) ? e.completedDate : e.eventDate).filter(Boolean).sort();
            if (dates.length < 2) return null;
            return (
              <Group justify="space-between" mt={4}>
                <Text size="xs" c="dimmed">{dates[0]}</Text>
                <Text size="xs" c="dimmed">{dates[dates.length - 1]}</Text>
              </Group>
            );
          })()}
        </Paper>
      )}

      {/* Vertical timeline with connector dots */}
      <Stack gap="xs">
        {events.map((ev, idx) => (
          <Group key={ev.Rid} gap="md" align="flex-start" wrap="nowrap">
            <Stack align="center" gap={0} style={{ minWidth: 40 }}>
              <ThemeIcon size={32} radius="xl" variant="light"
                color={ev.status === 'completed' ? 'green' : ev.eventType === 'deadline' ? 'red' : 'gray'}>
                <IconClock size={16} />
              </ThemeIcon>
              {idx < events.length - 1 && (
                <div style={{ width: 2, height: 40, background: 'var(--mantine-color-gray-3)' }} />
              )}
            </Stack>
            <Paper p="sm" withBorder style={{ flex: 1 }}>
              <Group justify="space-between" mb={4}>
                <Group gap="xs">
                  <Text size="sm" fw={600}>{ev.eventDate || '--'}</Text>
                  <Badge size="sm" variant="light" color="green">
                    {(ev.eventType || 'event').replace(/_/g, ' ')}
                  </Badge>
                </Group>
                <Group gap="xs">
                  {ev.status && (
                    <Badge size="sm" variant="light" color={ev.status === 'completed' ? 'green' : ev.status === 'pending' ? 'gray' : 'yellow'}>
                      {ev.status}
                    </Badge>
                  )}
                  <ActionIcon variant="subtle" color="red" size="sm" onClick={() => removeEvent(ev.Rid)}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              </Group>
              <Text size="sm">{ev.description}</Text>
              {ev.dueDate && <Text size="xs" c="dimmed" mt={2}>Due: {ev.dueDate}</Text>}
            </Paper>
          </Group>
        ))}
      </Stack>

      <Modal opened={showAdd} onClose={() => setShowAdd(false)} title="Add Timeline Event">
        <Stack gap="sm">
          <Select label="Event Type" data={EVENT_TYPE_OPTIONS} value={form.eventType || null}
            onChange={v => setForm(f => ({ ...f, eventType: v || '' }))} />
          <TextInput label="Event Date" type="date" value={form.eventDate}
            onChange={e => setForm(f => ({ ...f, eventDate: e.currentTarget.value }))} />
          <TextInput label="Due Date" type="date" value={form.dueDate}
            onChange={e => setForm(f => ({ ...f, dueDate: e.currentTarget.value }))} />
          <Textarea label="Description" required value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.currentTarget.value }))} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button color="green" onClick={addEvent}>Add</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}


// ────── Notes Tab ────────────────────────────────────────────

function NotesTab({ notes, projectId, createNote, deleteNote, userId }: {
  notes: PANote[]; projectId: string;
  createNote: (data: Partial<PANote>) => Promise<number>;
  deleteNote: (rid: number) => Promise<void>;
  userId: string;
}) {
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  async function addNote() {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await createNote({
        parentType: 'hmgp_project',
        parentId: projectId,
        content: content.trim(),
        creatorId: userId,
        createdDate: new Date().toISOString().slice(0, 10),
        visibility: 'internal',
      } as Partial<PANote>);
      notifications.show({ title: 'Added', message: 'Note added.', color: 'green' });
      setContent('');
    } catch { notifications.show({ title: 'Error', message: 'Failed to add note.', color: 'red' }); }
    setSaving(false);
  }

  async function removeNote(rid: number) {
    try {
      await deleteNote(rid);
      notifications.show({ title: 'Deleted', message: 'Note removed.', color: 'green' });
    } catch { notifications.show({ title: 'Error', message: 'Failed to delete.', color: 'red' }); }
  }

  return (
    <Stack gap="md">
      <Text fw={600} size="lg">Notes</Text>

      {/* Add Note */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Textarea placeholder="Add a note..." autosize minRows={2} value={content}
            onChange={e => setContent(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button color="green" size="sm" disabled={!content.trim()} loading={saving}
              onClick={addNote} leftSection={<IconPlus size={14} />}>Add Note</Button>
          </Group>
        </Stack>
      </Paper>

      {notes.length === 0 && (
        <Paper p="xl" withBorder ta="center">
          <Text c="dimmed">No notes yet.</Text>
        </Paper>
      )}

      <Stack gap="xs">
        {notes.map(n => (
          <Paper key={n.Rid} p="sm" withBorder>
            <Group justify="space-between" align="flex-start">
              <Stack gap={2} style={{ flex: 1 }}>
                <Group gap="xs">
                  <Text size="xs" fw={600}>{n.creatorId || 'Unknown'}</Text>
                  <Text size="xs" c="dimmed">{n.createdDate || ''}</Text>
                </Group>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{n.content}</Text>
              </Stack>
              <ActionIcon variant="subtle" color="red" size="sm" onClick={() => removeNote(n.Rid)}>
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}


// ────── Photos Tab ──────────────────────────────────────────

function PhotosTab({ docs, projectId, createDocument, deleteDocument, fetchDocuments }: {
  docs: PADocument[]; projectId: string;
  createDocument: (data: Partial<PADocument>) => Promise<number>;
  deleteDocument: (rid: number) => Promise<void>;
  fetchDocuments: () => Promise<void>;
}) {
  const photos = useMemo(() => docs.filter(d =>
    (d.mimeType && d.mimeType.includes('image')) || d.tags === 'photo'
  ), [docs]);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  function resizeImage(dataUrl: string, maxW: number): Promise<string> {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.55));
      };
      img.src = dataUrl;
    });
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const resized = await resizeImage(reader.result as string, 400);
        const base64 = resized.split(',')[1];
        await createDocument({
          projectId,
          documentName: file.name,
          documentType: 'Image',
          description: `Photo added ${new Date().toISOString().split('T')[0]}`,
          uploadedBy: '',
          uploadDate: new Date().toISOString().split('T')[0],
          tags: 'photo',
          notes: base64,
          mimeType: file.type || 'image/jpeg',
          fileSize: String(base64.length),
        } as Partial<PADocument>);
        await fetchDocuments();
        notifications.show({ title: 'Uploaded', message: 'Photo added.', color: 'green' });
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      notifications.show({ title: 'Error', message: 'Upload failed.', color: 'red' });
      setUploading(false);
    }
    e.target.value = '';
  }

  async function removePhoto(rid: number) {
    try {
      await deleteDocument(rid);
      await fetchDocuments();
      notifications.show({ title: 'Deleted', message: 'Photo removed.', color: 'green' });
    } catch { notifications.show({ title: 'Error', message: 'Failed to delete.', color: 'red' }); }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600} size="lg">Photos ({photos.length})</Text>
        <Group gap="xs">
          <Button color="green" variant="light" leftSection={<IconCamera size={16} />}
            loading={uploading} onClick={() => cameraRef.current?.click()}>Capture</Button>
          <Button color="green" leftSection={<IconUpload size={16} />}
            loading={uploading} onClick={() => fileRef.current?.click()}>Upload</Button>
        </Group>
      </Group>

      {photos.length === 0 && (
        <Paper p="xl" withBorder ta="center">
          <IconPhoto size={40} color="#9CA3AF" style={{ margin: '0 auto 8px' }} />
          <Text c="dimmed">No photos yet. Upload or capture a photo to get started.</Text>
        </Paper>
      )}

      <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }}>
        {photos.map(p => {
          const src = p.notes ? (p.notes.startsWith('data:') ? p.notes : `data:image/jpeg;base64,${p.notes}`) : '';
          return (
            <Paper key={p.Rid} withBorder p={4} style={{ position: 'relative', cursor: 'pointer' }}
              onClick={() => src && setPreviewUrl(src)}>
              {src
                ? <img src={src} alt={p.documentName} style={{ width: '100%', borderRadius: 4, display: 'block' }} />
                : <Center h={120} bg="gray.1" style={{ borderRadius: 4 }}><IconPhoto size={32} color="#9CA3AF" /></Center>
              }
              <Text size="xs" c="dimmed" ta="center" mt={4} lineClamp={1}>{p.documentName}</Text>
              <ActionIcon variant="filled" color="red" size="xs"
                style={{ position: 'absolute', top: 6, right: 6 }}
                onClick={e => { e.stopPropagation(); removePhoto(p.Rid); }}>
                <IconTrash size={12} />
              </ActionIcon>
            </Paper>
          );
        })}
      </SimpleGrid>

      <input type="file" ref={fileRef} style={{ display: 'none' }} accept="image/*" onChange={handleFile} />
      <input type="file" ref={cameraRef} style={{ display: 'none' }} accept="image/*" capture="environment" onChange={handleFile} />

      {/* Preview Modal */}
      <Modal opened={!!previewUrl} onClose={() => setPreviewUrl(null)} title="Photo Preview" size="lg">
        {previewUrl && <img src={previewUrl} alt="Preview" style={{ width: '100%', borderRadius: 8 }} />}
      </Modal>
    </Stack>
  );
}


// ────── Tasks Tab ───────────────────────────────────────────

const TASK_STATUS_COLORS: Record<string, string> = { pending: 'gray', in_progress: 'blue', completed: 'green' };
const TASK_STATUS_LABELS: Record<string, string> = { pending: 'Pending', in_progress: 'In Progress', completed: 'Completed' };
const TASK_STATUS_CYCLE = ['pending', 'in_progress', 'completed'];

const HMGP_TASK_TEMPLATES = [
  { description: 'Complete subapplication package', dueOffset: 30 },
  { description: 'Submit Benefit-Cost Analysis (BCA)', dueOffset: 45 },
  { description: 'EHP / NEPA review', dueOffset: 90 },
  { description: 'Engineering design completion', dueOffset: 120 },
  { description: 'Procurement (2 CFR 200 compliant)', dueOffset: 150 },
  { description: 'Begin construction / implementation', dueOffset: 180 },
  { description: 'Submit quarterly progress report', dueOffset: 210 },
  { description: 'Final inspection and certification', dueOffset: 330 },
  { description: 'Closeout package submission', dueOffset: 365 },
];

function TasksTab({ events, projectId, createTimelineEvent, deleteTimelineEvent, userId, organizationId }: {
  events: PATimelineEvent[]; projectId: string;
  createTimelineEvent: (data: Partial<PATimelineEvent>) => Promise<number>;
  deleteTimelineEvent: (rid: number) => Promise<void>;
  userId: string; organizationId: string;
}) {
  const tasks = useMemo(() => events.filter(e => e.eventType === 'task'), [events]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ description: '', dueDate: '', status: 'pending', assignedTo: '' });
  const [seeding, setSeeding] = useState(false);

  async function addTask() {
    if (!form.description) { notifications.show({ title: 'Required', message: 'Enter a description.', color: 'orange' }); return; }
    try {
      await createTimelineEvent({
        projectId,
        organizationId,
        eventType: 'task',
        eventDate: new Date().toISOString().slice(0, 10),
        description: form.description,
        dueDate: form.dueDate,
        status: form.status || 'pending',
        creatorId: userId,
      } as Partial<PATimelineEvent>);
      notifications.show({ title: 'Added', message: 'Task created.', color: 'green' });
      setForm({ description: '', dueDate: '', status: 'pending', assignedTo: '' });
      setShowAdd(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to create task.', color: 'red' }); }
  }

  async function toggleStatus(task: PATimelineEvent) {
    const cur = task.status || 'pending';
    const idx = TASK_STATUS_CYCLE.indexOf(cur);
    const next = TASK_STATUS_CYCLE[(idx + 1) % TASK_STATUS_CYCLE.length];
    try {
      await deleteTimelineEvent(task.Rid);
      await createTimelineEvent({
        projectId,
        organizationId,
        eventType: 'task',
        eventDate: task.eventDate,
        description: task.description,
        dueDate: task.dueDate,
        status: next,
        creatorId: task.creatorId,
      } as Partial<PATimelineEvent>);
    } catch { notifications.show({ title: 'Error', message: 'Failed to update task.', color: 'red' }); }
  }

  async function seedTemplates() {
    setSeeding(true);
    const today = new Date();
    try {
      for (const tmpl of HMGP_TASK_TEMPLATES) {
        const due = new Date(today);
        due.setDate(due.getDate() + tmpl.dueOffset);
        await createTimelineEvent({
          projectId,
          organizationId,
          eventType: 'task',
          eventDate: new Date().toISOString().slice(0, 10),
          description: tmpl.description,
          dueDate: due.toISOString().slice(0, 10),
          status: 'pending',
          creatorId: userId,
        } as Partial<PATimelineEvent>);
      }
      notifications.show({ title: 'Created', message: `${HMGP_TASK_TEMPLATES.length} standard tasks added.`, color: 'green' });
    } catch { notifications.show({ title: 'Error', message: 'Failed to seed tasks.', color: 'red' }); }
    setSeeding(false);
  }

  async function removeTask(rid: number) {
    try {
      await deleteTimelineEvent(rid);
      notifications.show({ title: 'Deleted', message: 'Task removed.', color: 'green' });
    } catch { notifications.show({ title: 'Error', message: 'Failed to delete.', color: 'red' }); }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600} size="lg">Tasks ({tasks.length})</Text>
        <Group gap="xs">
          <Button variant="light" color="green" leftSection={<IconListCheck size={16} />}
            loading={seeding} onClick={seedTemplates}>Standard HMGP Tasks</Button>
          <Button color="green" leftSection={<IconPlus size={16} />} onClick={() => setShowAdd(true)}>Add Task</Button>
        </Group>
      </Group>

      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Description</Table.Th>
            <Table.Th>Due Date</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th w={50}></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tasks.length === 0 && (
            <Table.Tr><Table.Td colSpan={4}><Text ta="center" c="dimmed" py="md">No tasks yet. Add one or use the standard template.</Text></Table.Td></Table.Tr>
          )}
          {tasks.map(t => {
            const isOverdue = t.dueDate && t.status !== 'completed' && t.dueDate < new Date().toISOString().slice(0, 10);
            return (
              <Table.Tr key={t.Rid}>
                <Table.Td>
                  <Text size="sm" td={t.status === 'completed' ? 'line-through' : undefined}
                    c={t.status === 'completed' ? 'dimmed' : undefined}>{t.description}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c={isOverdue ? 'red' : undefined} fw={isOverdue ? 600 : undefined}>
                    {t.dueDate || '--'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light" color={TASK_STATUS_COLORS[t.status || 'pending']}
                    style={{ cursor: 'pointer' }} onClick={() => toggleStatus(t)}>
                    {TASK_STATUS_LABELS[t.status || 'pending'] || t.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <ActionIcon variant="subtle" color="red" size="sm" onClick={() => removeTask(t.Rid)}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      <Modal opened={showAdd} onClose={() => setShowAdd(false)} title="Add Task">
        <Stack gap="sm">
          <Textarea label="Description" required value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.currentTarget.value }))} />
          <TextInput label="Due Date" type="date" value={form.dueDate}
            onChange={e => setForm(f => ({ ...f, dueDate: e.currentTarget.value }))} />
          <Select label="Status" data={TASK_STATUS_CYCLE.map(s => ({ value: s, label: TASK_STATUS_LABELS[s] }))}
            value={form.status} onChange={v => setForm(f => ({ ...f, status: v || 'pending' }))} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button color="green" onClick={addTask}>Add</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}


// ────── Contracts Tab ───────────────────────────────────────

// ═══════════════════════════════════════════════════════════════
// Cost Sub-Tab Components
// ═══════════════════════════════════════════════════════════════

const RATE_TYPE_OPTIONS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const CONTRACT_STATUS_OPTIONS_SUB = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'terminated', label: 'Terminated' },
];
const CONTRACT_STATUS_COLORS_SUB: Record<string, string> = {
  pending: 'yellow', active: 'green', completed: 'blue', terminated: 'red',
};

const PROCUREMENT_METHOD_OPTIONS = [
  { value: 'competitive_sealed_bid', label: 'Competitive Sealed Bid' },
  { value: 'competitive_proposal', label: 'Competitive Proposal' },
  { value: 'small_purchase', label: 'Small Purchase (<$250K)' },
  { value: 'sole_source', label: 'Sole Source' },
  { value: 'micro_purchase', label: 'Micro Purchase' },
];

const CONTRACT_TYPE_OPTIONS = [
  { value: 'fixed_price', label: 'Fixed Price' },
  { value: 'time_and_materials', label: 'Time & Materials' },
  { value: 'cost_reimbursement', label: 'Cost Reimbursement' },
  { value: 'unit_price', label: 'Unit Price' },
];

// ────── Labor Sub-Tab ───────────────────────────────────────
function LaborSubTab({ records, projectId, organizationId, createLabor, updateLabor, deleteLabor }: {
  records: PALaborRecord[]; projectId: string; organizationId: string;
  createLabor: (data: Partial<PALaborRecord>) => Promise<number>;
  updateLabor: (rid: number, data: Partial<PALaborRecord>) => Promise<void>;
  deleteLabor: (rid: number) => Promise<void>;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PALaborRecord | null>(null);
  const empty = { employeeName: '', employeeTitle: '', workDate: '', hoursRegular: '', hoursOvertime: '',
    rateRegular: '', rateOvertime: '', fringeCost: '', laborCost: '', totalCost: '', activityDescription: '', notes: '' };
  const [form, setForm] = useState<Record<string, string>>({ ...empty });

  function openAdd() { setEditing(null); setForm({ ...empty }); setModalOpen(true); }
  function openEdit(r: PALaborRecord) {
    setEditing(r);
    setForm({ employeeName: r.employeeName || '', employeeTitle: r.employeeTitle || '', workDate: r.workDate || '',
      hoursRegular: r.hoursRegular || '', hoursOvertime: r.hoursOvertime || '',
      rateRegular: r.rateRegular || '', rateOvertime: r.rateOvertime || '',
      fringeCost: r.fringeCost || '', laborCost: r.laborCost || '', totalCost: r.totalCost || '',
      activityDescription: r.activityDescription || '', notes: r.notes || '' });
    setModalOpen(true);
  }

  // Auto-calc
  const regHrs = parseFloat(form.hoursRegular || '0') || 0;
  const otHrs = parseFloat(form.hoursOvertime || '0') || 0;
  const regRate = parseFloat(stripCurrency(form.rateRegular || '0')) || 0;
  const otRate = parseFloat(stripCurrency(form.rateOvertime || '0')) || 0;
  const calcLabor = regHrs * regRate + otHrs * otRate;
  const calcFringe = parseFloat(stripCurrency(form.fringeCost || '0')) || 0;
  const calcTotal = calcLabor + calcFringe;

  async function handleSave() {
    if (!form.employeeName) { notifications.show({ message: 'Employee name required', color: 'yellow' }); return; }
    const data: Partial<PALaborRecord> = {
      projectId, organizationId,
      employeeName: form.employeeName, employeeTitle: form.employeeTitle, workDate: form.workDate,
      hoursRegular: form.hoursRegular, hoursOvertime: form.hoursOvertime,
      rateRegular: stripCurrency(form.rateRegular), rateOvertime: stripCurrency(form.rateOvertime),
      fringeCost: String(calcFringe), laborCost: String(calcLabor), totalCost: String(calcTotal),
      activityDescription: form.activityDescription, notes: form.notes,
    };
    try {
      if (editing) await updateLabor(editing.Rid, data);
      else await createLabor(data);
      notifications.show({ title: 'Saved', message: 'Labor record saved.', color: 'green' });
      setModalOpen(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to save.', color: 'red' }); }
  }

  const total = records.reduce((s, r) => s + (parseFloat(r.totalCost || '0') || 0), 0);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="xs"><Text fw={600} size="lg">Labor Records</Text><Badge color="green" variant="light">Total: {fmt(total)}</Badge></Group>
        <Button color="green" leftSection={<IconPlus size={16} />} onClick={openAdd}>Add Labor</Button>
      </Group>
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead><Table.Tr>
          <Table.Th>Employee</Table.Th><Table.Th>Title</Table.Th><Table.Th>Date</Table.Th>
          <Table.Th ta="right">Reg Hrs</Table.Th><Table.Th ta="right">OT Hrs</Table.Th>
          <Table.Th ta="right">Reg Rate</Table.Th><Table.Th ta="right">OT Rate</Table.Th>
          <Table.Th ta="right">Labor $</Table.Th><Table.Th ta="right">Fringe $</Table.Th>
          <Table.Th ta="right">Total</Table.Th><Table.Th w={50}></Table.Th>
        </Table.Tr></Table.Thead>
        <Table.Tbody>
          {records.length === 0 && <Table.Tr><Table.Td colSpan={11}><Text ta="center" c="dimmed" py="md">No labor records yet.</Text></Table.Td></Table.Tr>}
          {records.map(r => (
            <Table.Tr key={r.Rid} style={{ cursor: 'pointer' }} onClick={() => openEdit(r)}>
              <Table.Td fw={500}>{r.employeeName || '--'}</Table.Td>
              <Table.Td>{r.employeeTitle || '--'}</Table.Td>
              <Table.Td>{r.workDate || '--'}</Table.Td>
              <Table.Td ta="right">{r.hoursRegular || '--'}</Table.Td>
              <Table.Td ta="right">{r.hoursOvertime || '--'}</Table.Td>
              <Table.Td ta="right">{r.rateRegular ? formatCurrency(r.rateRegular) : '--'}</Table.Td>
              <Table.Td ta="right">{r.rateOvertime ? formatCurrency(r.rateOvertime) : '--'}</Table.Td>
              <Table.Td ta="right">{r.laborCost ? formatCurrency(r.laborCost) : '--'}</Table.Td>
              <Table.Td ta="right">{r.fringeCost ? formatCurrency(r.fringeCost) : '--'}</Table.Td>
              <Table.Td ta="right" fw={600}>{r.totalCost ? formatCurrency(r.totalCost) : '--'}</Table.Td>
              <Table.Td><ActionIcon variant="subtle" color="red" size="sm" onClick={e => { e.stopPropagation(); deleteLabor(r.Rid); }}><IconTrash size={14} /></ActionIcon></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Labor' : 'Add Labor'} size="lg">
        <Stack gap="sm">
          <Group grow>
            <TextInput label="Employee Name" required value={form.employeeName} onChange={e => setForm(f => ({ ...f, employeeName: e.currentTarget.value }))} />
            <TextInput label="Title" value={form.employeeTitle} onChange={e => setForm(f => ({ ...f, employeeTitle: e.currentTarget.value }))} />
          </Group>
          <TextInput label="Work Date" type="date" value={form.workDate} onChange={e => setForm(f => ({ ...f, workDate: e.currentTarget.value }))} />
          <Group grow>
            <TextInput label="Regular Hours" value={form.hoursRegular} onChange={e => setForm(f => ({ ...f, hoursRegular: e.currentTarget.value }))} />
            <TextInput label="OT Hours" value={form.hoursOvertime} onChange={e => setForm(f => ({ ...f, hoursOvertime: e.currentTarget.value }))} />
          </Group>
          <Group grow>
            <TextInput label="Regular Rate" value={formatCurrency(form.rateRegular)} onChange={e => setForm(f => ({ ...f, rateRegular: stripCurrency(e.currentTarget.value) }))} styles={{ input: { fontFamily: 'monospace' } }} />
            <TextInput label="OT Rate" value={formatCurrency(form.rateOvertime)} onChange={e => setForm(f => ({ ...f, rateOvertime: stripCurrency(e.currentTarget.value) }))} styles={{ input: { fontFamily: 'monospace' } }} />
          </Group>
          <TextInput label="Fringe Cost" value={formatCurrency(form.fringeCost)} onChange={e => setForm(f => ({ ...f, fringeCost: stripCurrency(e.currentTarget.value) }))} styles={{ input: { fontFamily: 'monospace' } }} />
          <Paper p="xs" withBorder style={{ background: '#f0fdf4' }}>
            <Group gap="lg">
              <Text size="sm" c="dimmed">Labor Cost: <Text span fw={600}>{fmt(calcLabor)}</Text></Text>
              <Text size="sm" c="dimmed">Fringe: <Text span fw={600}>{fmt(calcFringe)}</Text></Text>
              <Text size="sm" fw={700} c="green.8">Total: {fmt(calcTotal)}</Text>
            </Group>
          </Paper>
          <Textarea label="Activity Description" value={form.activityDescription} onChange={e => setForm(f => ({ ...f, activityDescription: e.currentTarget.value }))} />
          <Textarea label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.currentTarget.value }))} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button color="green" onClick={handleSave}>{editing ? 'Save' : 'Add'}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// ────── Equipment Sub-Tab ───────────────────────────────────
function EquipmentSubTab({ records, projectId, organizationId, createEquip, updateEquip, deleteEquip }: {
  records: PAEquipRecord[]; projectId: string; organizationId: string;
  createEquip: (data: Partial<PAEquipRecord>) => Promise<number>;
  updateEquip: (rid: number, data: Partial<PAEquipRecord>) => Promise<void>;
  deleteEquip: (rid: number) => Promise<void>;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PAEquipRecord | null>(null);
  const empty = { equipmentType: '', equipmentSize: '', femaEquipCode: '', workDate: '', hoursUsed: '', hourlyRate: '', totalCost: '', activityDescription: '', notes: '' };
  const [form, setForm] = useState<Record<string, string>>({ ...empty });

  function openAdd() { setEditing(null); setForm({ ...empty }); setModalOpen(true); }
  function openEdit(r: PAEquipRecord) {
    setEditing(r);
    setForm({ equipmentType: r.equipmentType || '', equipmentSize: r.equipmentSize || '', femaEquipCode: r.femaEquipCode || '',
      workDate: r.workDate || '', hoursUsed: r.hoursUsed || '', hourlyRate: r.hourlyRate || '',
      totalCost: r.totalCost || '', activityDescription: r.activityDescription || '', notes: r.notes || '' });
    setModalOpen(true);
  }

  const hrs = parseFloat(form.hoursUsed || '0') || 0;
  const rate = parseFloat(stripCurrency(form.hourlyRate || '0')) || 0;
  const calcTotal = hrs * rate;

  async function handleSave() {
    if (!form.equipmentType) { notifications.show({ message: 'Equipment type required', color: 'yellow' }); return; }
    const data: Partial<PAEquipRecord> = {
      projectId, organizationId,
      equipmentType: form.equipmentType, equipmentSize: form.equipmentSize, femaEquipCode: form.femaEquipCode,
      workDate: form.workDate, hoursUsed: form.hoursUsed,
      hourlyRate: stripCurrency(form.hourlyRate), totalCost: String(calcTotal),
      activityDescription: form.activityDescription, notes: form.notes,
    };
    try {
      if (editing) await updateEquip(editing.Rid, data);
      else await createEquip(data);
      notifications.show({ title: 'Saved', message: 'Equipment record saved.', color: 'green' });
      setModalOpen(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to save.', color: 'red' }); }
  }

  const total = records.reduce((s, r) => s + (parseFloat(r.totalCost || '0') || 0), 0);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="xs"><Text fw={600} size="lg">Equipment Records</Text><Badge color="green" variant="light">Total: {fmt(total)}</Badge></Group>
        <Button color="green" leftSection={<IconPlus size={16} />} onClick={openAdd}>Add Equipment</Button>
      </Group>
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead><Table.Tr>
          <Table.Th>Equipment Type</Table.Th><Table.Th>Size</Table.Th><Table.Th>FEMA Code</Table.Th>
          <Table.Th>Date</Table.Th><Table.Th ta="right">Hours</Table.Th><Table.Th ta="right">Rate</Table.Th>
          <Table.Th ta="right">Total</Table.Th><Table.Th w={50}></Table.Th>
        </Table.Tr></Table.Thead>
        <Table.Tbody>
          {records.length === 0 && <Table.Tr><Table.Td colSpan={8}><Text ta="center" c="dimmed" py="md">No equipment records yet.</Text></Table.Td></Table.Tr>}
          {records.map(r => (
            <Table.Tr key={r.Rid} style={{ cursor: 'pointer' }} onClick={() => openEdit(r)}>
              <Table.Td fw={500}>{r.equipmentType || '--'}</Table.Td>
              <Table.Td>{r.equipmentSize || '--'}</Table.Td>
              <Table.Td>{r.femaEquipCode || '--'}</Table.Td>
              <Table.Td>{r.workDate || '--'}</Table.Td>
              <Table.Td ta="right">{r.hoursUsed || '--'}</Table.Td>
              <Table.Td ta="right">{r.hourlyRate ? formatCurrency(r.hourlyRate) : '--'}</Table.Td>
              <Table.Td ta="right" fw={600}>{r.totalCost ? formatCurrency(r.totalCost) : '--'}</Table.Td>
              <Table.Td><ActionIcon variant="subtle" color="red" size="sm" onClick={e => { e.stopPropagation(); deleteEquip(r.Rid); }}><IconTrash size={14} /></ActionIcon></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Equipment' : 'Add Equipment'} size="lg">
        <Stack gap="sm">
          <Group grow>
            <TextInput label="Equipment Type" required value={form.equipmentType} onChange={e => setForm(f => ({ ...f, equipmentType: e.currentTarget.value }))} />
            <TextInput label="Size/Class" value={form.equipmentSize} onChange={e => setForm(f => ({ ...f, equipmentSize: e.currentTarget.value }))} />
          </Group>
          <Group grow>
            <TextInput label="FEMA Equipment Code" value={form.femaEquipCode} onChange={e => setForm(f => ({ ...f, femaEquipCode: e.currentTarget.value }))} />
            <TextInput label="Work Date" type="date" value={form.workDate} onChange={e => setForm(f => ({ ...f, workDate: e.currentTarget.value }))} />
          </Group>
          <Group grow>
            <TextInput label="Hours Used" value={form.hoursUsed} onChange={e => setForm(f => ({ ...f, hoursUsed: e.currentTarget.value }))} />
            <TextInput label="Hourly Rate" value={formatCurrency(form.hourlyRate)} onChange={e => setForm(f => ({ ...f, hourlyRate: stripCurrency(e.currentTarget.value) }))} styles={{ input: { fontFamily: 'monospace' } }} />
          </Group>
          <Paper p="xs" withBorder style={{ background: '#f0fdf4' }}>
            <Text size="sm" fw={700} c="green.8">Calculated Total: {fmt(calcTotal)}</Text>
          </Paper>
          <Textarea label="Activity Description" value={form.activityDescription} onChange={e => setForm(f => ({ ...f, activityDescription: e.currentTarget.value }))} />
          <Textarea label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.currentTarget.value }))} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button color="green" onClick={handleSave}>{editing ? 'Save' : 'Add'}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// ────── Materials Sub-Tab ───────────────────────────────────
function MaterialsSubTab({ records, projectId, organizationId, createMaterial, updateMaterial, deleteMaterial }: {
  records: PAMaterial[]; projectId: string; organizationId: string;
  createMaterial: (data: Partial<PAMaterial>) => Promise<number>;
  updateMaterial: (rid: number, data: Partial<PAMaterial>) => Promise<void>;
  deleteMaterial: (rid: number) => Promise<void>;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PAMaterial | null>(null);
  const empty = { materialDescription: '', vendor: '', purchaseDate: '', quantity: '', unit: '', unitCost: '', totalCost: '', invoiceNumber: '', notes: '' };
  const [form, setForm] = useState<Record<string, string>>({ ...empty });

  function openAdd() { setEditing(null); setForm({ ...empty }); setModalOpen(true); }
  function openEdit(r: PAMaterial) {
    setEditing(r);
    setForm({ materialDescription: r.materialDescription || '', vendor: r.vendor || '', purchaseDate: r.purchaseDate || '',
      quantity: r.quantity || '', unit: r.unit || '', unitCost: r.unitCost || '',
      totalCost: r.totalCost || '', invoiceNumber: r.invoiceNumber || '', notes: r.notes || '' });
    setModalOpen(true);
  }

  const qty = parseFloat(form.quantity || '0') || 0;
  const uc = parseFloat(stripCurrency(form.unitCost || '0')) || 0;
  const calcTotal = qty * uc;

  async function handleSave() {
    if (!form.materialDescription) { notifications.show({ message: 'Description required', color: 'yellow' }); return; }
    const data: Partial<PAMaterial> = {
      projectId, organizationId,
      materialDescription: form.materialDescription, vendor: form.vendor, purchaseDate: form.purchaseDate,
      quantity: form.quantity, unit: form.unit, unitCost: stripCurrency(form.unitCost),
      totalCost: String(calcTotal), invoiceNumber: form.invoiceNumber, notes: form.notes,
    };
    try {
      if (editing) await updateMaterial(editing.Rid, data);
      else await createMaterial(data);
      notifications.show({ title: 'Saved', message: 'Material record saved.', color: 'green' });
      setModalOpen(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to save.', color: 'red' }); }
  }

  const total = records.reduce((s, r) => s + (parseFloat(r.totalCost || '0') || 0), 0);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="xs"><Text fw={600} size="lg">Materials</Text><Badge color="green" variant="light">Total: {fmt(total)}</Badge></Group>
        <Button color="green" leftSection={<IconPlus size={16} />} onClick={openAdd}>Add Material</Button>
      </Group>
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead><Table.Tr>
          <Table.Th>Description</Table.Th><Table.Th>Vendor</Table.Th><Table.Th>Date</Table.Th>
          <Table.Th ta="right">Qty</Table.Th><Table.Th>Unit</Table.Th><Table.Th ta="right">Unit Cost</Table.Th>
          <Table.Th ta="right">Total</Table.Th><Table.Th>Invoice #</Table.Th><Table.Th w={50}></Table.Th>
        </Table.Tr></Table.Thead>
        <Table.Tbody>
          {records.length === 0 && <Table.Tr><Table.Td colSpan={9}><Text ta="center" c="dimmed" py="md">No materials yet.</Text></Table.Td></Table.Tr>}
          {records.map(r => (
            <Table.Tr key={r.Rid} style={{ cursor: 'pointer' }} onClick={() => openEdit(r)}>
              <Table.Td fw={500}>{r.materialDescription || '--'}</Table.Td>
              <Table.Td>{r.vendor || '--'}</Table.Td>
              <Table.Td>{r.purchaseDate || '--'}</Table.Td>
              <Table.Td ta="right">{r.quantity || '--'}</Table.Td>
              <Table.Td>{r.unit || '--'}</Table.Td>
              <Table.Td ta="right">{r.unitCost ? formatCurrency(r.unitCost) : '--'}</Table.Td>
              <Table.Td ta="right" fw={600}>{r.totalCost ? formatCurrency(r.totalCost) : '--'}</Table.Td>
              <Table.Td>{r.invoiceNumber || '--'}</Table.Td>
              <Table.Td><ActionIcon variant="subtle" color="red" size="sm" onClick={e => { e.stopPropagation(); deleteMaterial(r.Rid); }}><IconTrash size={14} /></ActionIcon></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Material' : 'Add Material'} size="lg">
        <Stack gap="sm">
          <TextInput label="Description" required value={form.materialDescription} onChange={e => setForm(f => ({ ...f, materialDescription: e.currentTarget.value }))} />
          <Group grow>
            <TextInput label="Vendor" value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.currentTarget.value }))} />
            <TextInput label="Purchase Date" type="date" value={form.purchaseDate} onChange={e => setForm(f => ({ ...f, purchaseDate: e.currentTarget.value }))} />
          </Group>
          <Group grow>
            <TextInput label="Quantity" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.currentTarget.value }))} />
            <TextInput label="Unit" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.currentTarget.value }))} placeholder="e.g. CY, LF, each" />
            <TextInput label="Unit Cost" value={formatCurrency(form.unitCost)} onChange={e => setForm(f => ({ ...f, unitCost: stripCurrency(e.currentTarget.value) }))} styles={{ input: { fontFamily: 'monospace' } }} />
          </Group>
          <Paper p="xs" withBorder style={{ background: '#f0fdf4' }}>
            <Text size="sm" fw={700} c="green.8">Calculated Total: {fmt(calcTotal)}</Text>
          </Paper>
          <TextInput label="Invoice #" value={form.invoiceNumber} onChange={e => setForm(f => ({ ...f, invoiceNumber: e.currentTarget.value }))} />
          <Textarea label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.currentTarget.value }))} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button color="green" onClick={handleSave}>{editing ? 'Save' : 'Add'}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// ────── Contracts Sub-Tab ───────────────────────────────────
function ContractsSubTab({ records, projectId, organizationId, createContract, updateContract, deleteContract }: {
  records: PAContract[]; projectId: string; organizationId: string;
  createContract: (data: Partial<PAContract>) => Promise<number>;
  updateContract: (rid: number, data: Partial<PAContract>) => Promise<void>;
  deleteContract: (rid: number) => Promise<void>;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PAContract | null>(null);
  const empty = { contractorName: '', contractNumber: '', contractType: '', procurementMethod: '',
    contractAmount: '', changeOrderCount: '', changeOrderAmount: '', totalContractValue: '', amountPaid: '',
    scopeOfWork: '', startDate: '', endDate: '', davisBaconApplicable: '', bondingRequired: '', status: 'pending', notes: '' };
  const [form, setForm] = useState<Record<string, string>>({ ...empty });

  function openAdd() { setEditing(null); setForm({ ...empty }); setModalOpen(true); }
  function openEdit(r: PAContract) {
    setEditing(r);
    setForm({ contractorName: r.contractorName || '', contractNumber: r.contractNumber || '', contractType: r.contractType || '',
      procurementMethod: r.procurementMethod || '', contractAmount: r.contractAmount || '',
      changeOrderCount: r.changeOrderCount || '', changeOrderAmount: r.changeOrderAmount || '',
      totalContractValue: r.totalContractValue || '', amountPaid: r.amountPaid || '',
      scopeOfWork: r.scopeOfWork || '', startDate: r.startDate || '', endDate: r.endDate || '',
      davisBaconApplicable: r.davisBaconApplicable || '', bondingRequired: r.bondingRequired || '',
      status: r.status || 'pending', notes: r.notes || '' });
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.contractorName) { notifications.show({ message: 'Contractor name required', color: 'yellow' }); return; }
    const data: Partial<PAContract> = {
      projectId, organizationId,
      contractorName: form.contractorName, contractNumber: form.contractNumber, contractType: form.contractType,
      procurementMethod: form.procurementMethod, contractAmount: stripCurrency(form.contractAmount),
      changeOrderCount: form.changeOrderCount, changeOrderAmount: stripCurrency(form.changeOrderAmount),
      totalContractValue: stripCurrency(form.totalContractValue), amountPaid: stripCurrency(form.amountPaid),
      scopeOfWork: form.scopeOfWork, startDate: form.startDate, endDate: form.endDate,
      davisBaconApplicable: form.davisBaconApplicable, bondingRequired: form.bondingRequired,
      status: form.status, notes: form.notes,
    };
    try {
      if (editing) await updateContract(editing.Rid, data);
      else await createContract(data);
      notifications.show({ title: 'Saved', message: 'Contract saved.', color: 'green' });
      setModalOpen(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to save.', color: 'red' }); }
  }

  const totalPaid = records.reduce((s, r) => s + (parseFloat(r.amountPaid || '0') || 0), 0);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="xs"><Text fw={600} size="lg">Contracts</Text><Badge color="green" variant="light">Paid: {fmt(totalPaid)}</Badge></Group>
        <Button color="green" leftSection={<IconPlus size={16} />} onClick={openAdd}>Add Contract</Button>
      </Group>
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead><Table.Tr>
          <Table.Th>Contractor</Table.Th><Table.Th>Contract #</Table.Th><Table.Th>Type</Table.Th>
          <Table.Th>Procurement</Table.Th><Table.Th ta="right">Amount</Table.Th><Table.Th ta="right">Paid</Table.Th>
          <Table.Th>Scope</Table.Th><Table.Th>Status</Table.Th><Table.Th w={50}></Table.Th>
        </Table.Tr></Table.Thead>
        <Table.Tbody>
          {records.length === 0 && <Table.Tr><Table.Td colSpan={9}><Text ta="center" c="dimmed" py="md">No contracts yet.</Text></Table.Td></Table.Tr>}
          {records.map(r => (
            <Table.Tr key={r.Rid} style={{ cursor: 'pointer' }} onClick={() => openEdit(r)}>
              <Table.Td fw={500}>{r.contractorName || '--'}</Table.Td>
              <Table.Td>{r.contractNumber || '--'}</Table.Td>
              <Table.Td>{CONTRACT_TYPE_OPTIONS.find(o => o.value === r.contractType)?.label || r.contractType || '--'}</Table.Td>
              <Table.Td><Text size="sm" lineClamp={1}>{PROCUREMENT_METHOD_OPTIONS.find(o => o.value === r.procurementMethod)?.label || r.procurementMethod || '--'}</Text></Table.Td>
              <Table.Td ta="right" fw={600}>{r.contractAmount ? formatCurrency(r.contractAmount) : '--'}</Table.Td>
              <Table.Td ta="right">{r.amountPaid ? formatCurrency(r.amountPaid) : '--'}</Table.Td>
              <Table.Td><Text size="sm" lineClamp={1}>{r.scopeOfWork || '--'}</Text></Table.Td>
              <Table.Td>
                <Badge size="sm" variant="light" color={CONTRACT_STATUS_COLORS_SUB[r.status] || 'gray'}>
                  {CONTRACT_STATUS_OPTIONS_SUB.find(o => o.value === r.status)?.label || r.status}
                </Badge>
              </Table.Td>
              <Table.Td><ActionIcon variant="subtle" color="red" size="sm" onClick={e => { e.stopPropagation(); deleteContract(r.Rid); }}><IconTrash size={14} /></ActionIcon></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Contract' : 'Add Contract'} size="xl">
        <Stack gap="sm">
          <Group grow>
            <TextInput label="Contractor Name" required value={form.contractorName} onChange={e => setForm(f => ({ ...f, contractorName: e.currentTarget.value }))} />
            <TextInput label="Contract Number" value={form.contractNumber} onChange={e => setForm(f => ({ ...f, contractNumber: e.currentTarget.value }))} />
          </Group>
          <Group grow>
            <Select label="Contract Type" data={CONTRACT_TYPE_OPTIONS} value={form.contractType || null} onChange={v => setForm(f => ({ ...f, contractType: v || '' }))} />
            <Select label="Procurement Method" data={PROCUREMENT_METHOD_OPTIONS} value={form.procurementMethod || null} onChange={v => setForm(f => ({ ...f, procurementMethod: v || '' }))} />
          </Group>
          <Group grow>
            <TextInput label="Contract Amount" value={formatCurrency(form.contractAmount)} onChange={e => setForm(f => ({ ...f, contractAmount: stripCurrency(e.currentTarget.value) }))} styles={{ input: { fontFamily: 'monospace' } }} />
            <TextInput label="Amount Paid" value={formatCurrency(form.amountPaid)} onChange={e => setForm(f => ({ ...f, amountPaid: stripCurrency(e.currentTarget.value) }))} styles={{ input: { fontFamily: 'monospace' } }} />
          </Group>
          <Group grow>
            <TextInput label="Change Orders" value={form.changeOrderCount} onChange={e => setForm(f => ({ ...f, changeOrderCount: e.currentTarget.value }))} />
            <TextInput label="Change Order Amount" value={formatCurrency(form.changeOrderAmount)} onChange={e => setForm(f => ({ ...f, changeOrderAmount: stripCurrency(e.currentTarget.value) }))} styles={{ input: { fontFamily: 'monospace' } }} />
            <TextInput label="Total Contract Value" value={formatCurrency(form.totalContractValue)} onChange={e => setForm(f => ({ ...f, totalContractValue: stripCurrency(e.currentTarget.value) }))} styles={{ input: { fontFamily: 'monospace' } }} />
          </Group>
          <Textarea label="Scope of Work" value={form.scopeOfWork} onChange={e => setForm(f => ({ ...f, scopeOfWork: e.currentTarget.value }))} />
          <Group grow>
            <TextInput label="Start Date" type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.currentTarget.value }))} />
            <TextInput label="End Date" type="date" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.currentTarget.value }))} />
          </Group>
          <Group grow>
            <Select label="Davis-Bacon Applicable" data={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={form.davisBaconApplicable || null} onChange={v => setForm(f => ({ ...f, davisBaconApplicable: v || '' }))} />
            <Select label="Bonding Required" data={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={form.bondingRequired || null} onChange={v => setForm(f => ({ ...f, bondingRequired: v || '' }))} />
            <Select label="Status" data={CONTRACT_STATUS_OPTIONS_SUB} value={form.status || null} onChange={v => setForm(f => ({ ...f, status: v || 'pending' }))} />
          </Group>
          <Textarea label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.currentTarget.value }))} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button color="green" onClick={handleSave}>{editing ? 'Save' : 'Add'}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// ────── Rented Equipment Sub-Tab ────────────────────────────
function RentedSubTab({ records, projectId, organizationId, createRented, updateRented, deleteRented }: {
  records: PARentedEquip[]; projectId: string; organizationId: string;
  createRented: (data: Partial<PARentedEquip>) => Promise<number>;
  updateRented: (rid: number, data: Partial<PARentedEquip>) => Promise<void>;
  deleteRented: (rid: number) => Promise<void>;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PARentedEquip | null>(null);
  const empty = { equipmentType: '', vendor: '', rentalAgreementNumber: '', rateType: 'hourly', rate: '', hoursOrDaysUsed: '', totalCost: '', activityDescription: '', notes: '' };
  const [form, setForm] = useState<Record<string, string>>({ ...empty });

  function openAdd() { setEditing(null); setForm({ ...empty }); setModalOpen(true); }
  function openEdit(r: PARentedEquip) {
    setEditing(r);
    setForm({ equipmentType: r.equipmentType || '', vendor: r.vendor || '', rentalAgreementNumber: r.rentalAgreementNumber || '',
      rateType: r.rateType || 'hourly', rate: r.rate || '', hoursOrDaysUsed: r.hoursOrDaysUsed || '',
      totalCost: r.totalCost || '', activityDescription: r.activityDescription || '', notes: r.notes || '' });
    setModalOpen(true);
  }

  const usage = parseFloat(form.hoursOrDaysUsed || '0') || 0;
  const rate = parseFloat(stripCurrency(form.rate || '0')) || 0;
  const calcTotal = usage * rate;

  async function handleSave() {
    if (!form.equipmentType) { notifications.show({ message: 'Equipment type required', color: 'yellow' }); return; }
    const data: Partial<PARentedEquip> = {
      projectId, organizationId,
      equipmentType: form.equipmentType, vendor: form.vendor, rentalAgreementNumber: form.rentalAgreementNumber,
      rateType: form.rateType, rate: stripCurrency(form.rate), hoursOrDaysUsed: form.hoursOrDaysUsed,
      totalCost: String(calcTotal), activityDescription: form.activityDescription, notes: form.notes,
    };
    try {
      if (editing) await updateRented(editing.Rid, data);
      else await createRented(data);
      notifications.show({ title: 'Saved', message: 'Rented equipment record saved.', color: 'green' });
      setModalOpen(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to save.', color: 'red' }); }
  }

  const total = records.reduce((s, r) => s + (parseFloat(r.totalCost || '0') || 0), 0);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="xs"><Text fw={600} size="lg">Rented Equipment</Text><Badge color="green" variant="light">Total: {fmt(total)}</Badge></Group>
        <Button color="green" leftSection={<IconPlus size={16} />} onClick={openAdd}>Add Rented Equipment</Button>
      </Group>
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead><Table.Tr>
          <Table.Th>Equipment Type</Table.Th><Table.Th>Vendor</Table.Th><Table.Th>Agreement #</Table.Th>
          <Table.Th>Rate Type</Table.Th><Table.Th ta="right">Rate</Table.Th><Table.Th ta="right">Hrs/Days</Table.Th>
          <Table.Th ta="right">Total</Table.Th><Table.Th w={50}></Table.Th>
        </Table.Tr></Table.Thead>
        <Table.Tbody>
          {records.length === 0 && <Table.Tr><Table.Td colSpan={8}><Text ta="center" c="dimmed" py="md">No rented equipment yet.</Text></Table.Td></Table.Tr>}
          {records.map(r => (
            <Table.Tr key={r.Rid} style={{ cursor: 'pointer' }} onClick={() => openEdit(r)}>
              <Table.Td fw={500}>{r.equipmentType || '--'}</Table.Td>
              <Table.Td>{r.vendor || '--'}</Table.Td>
              <Table.Td>{r.rentalAgreementNumber || '--'}</Table.Td>
              <Table.Td>{RATE_TYPE_OPTIONS.find(o => o.value === r.rateType)?.label || r.rateType || '--'}</Table.Td>
              <Table.Td ta="right">{r.rate ? formatCurrency(r.rate) : '--'}</Table.Td>
              <Table.Td ta="right">{r.hoursOrDaysUsed || '--'}</Table.Td>
              <Table.Td ta="right" fw={600}>{r.totalCost ? formatCurrency(r.totalCost) : '--'}</Table.Td>
              <Table.Td><ActionIcon variant="subtle" color="red" size="sm" onClick={e => { e.stopPropagation(); deleteRented(r.Rid); }}><IconTrash size={14} /></ActionIcon></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Rented Equipment' : 'Add Rented Equipment'} size="lg">
        <Stack gap="sm">
          <Group grow>
            <TextInput label="Equipment Type" required value={form.equipmentType} onChange={e => setForm(f => ({ ...f, equipmentType: e.currentTarget.value }))} />
            <TextInput label="Vendor" value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.currentTarget.value }))} />
          </Group>
          <Group grow>
            <TextInput label="Rental Agreement #" value={form.rentalAgreementNumber} onChange={e => setForm(f => ({ ...f, rentalAgreementNumber: e.currentTarget.value }))} />
            <Select label="Rate Type" data={RATE_TYPE_OPTIONS} value={form.rateType || null} onChange={v => setForm(f => ({ ...f, rateType: v || 'hourly' }))} />
          </Group>
          <Group grow>
            <TextInput label="Rate" value={formatCurrency(form.rate)} onChange={e => setForm(f => ({ ...f, rate: stripCurrency(e.currentTarget.value) }))} styles={{ input: { fontFamily: 'monospace' } }} />
            <TextInput label="Hours/Days Used" value={form.hoursOrDaysUsed} onChange={e => setForm(f => ({ ...f, hoursOrDaysUsed: e.currentTarget.value }))} />
          </Group>
          <Paper p="xs" withBorder style={{ background: '#f0fdf4' }}>
            <Text size="sm" fw={700} c="green.8">Calculated Total: {fmt(calcTotal)}</Text>
          </Paper>
          <Textarea label="Activity Description" value={form.activityDescription} onChange={e => setForm(f => ({ ...f, activityDescription: e.currentTarget.value }))} />
          <Textarea label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.currentTarget.value }))} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button color="green" onClick={handleSave}>{editing ? 'Save' : 'Add'}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// ────── Donated Resources Sub-Tab ───────────────────────────
function DonatedSubTab({ records, projectId, organizationId, createDonated, updateDonated, deleteDonated }: {
  records: PADonatedResource[]; projectId: string; organizationId: string;
  createDonated: (data: Partial<PADonatedResource>) => Promise<number>;
  updateDonated: (rid: number, data: Partial<PADonatedResource>) => Promise<void>;
  deleteDonated: (rid: number) => Promise<void>;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PADonatedResource | null>(null);
  const empty = { resourceType: '', donorName: '', donorOrganization: '', workDate: '', hoursWorked: '', description: '', estimatedValue: '', notes: '' };
  const [form, setForm] = useState<Record<string, string>>({ ...empty });

  function openAdd() { setEditing(null); setForm({ ...empty }); setModalOpen(true); }
  function openEdit(r: PADonatedResource) {
    setEditing(r);
    setForm({ resourceType: r.resourceType || '', donorName: r.donorName || '', donorOrganization: r.donorOrganization || '',
      workDate: r.workDate || '', hoursWorked: r.hoursWorked || '', description: r.description || '',
      estimatedValue: r.estimatedValue || '', notes: r.notes || '' });
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.resourceType) { notifications.show({ message: 'Resource type required', color: 'yellow' }); return; }
    const data: Partial<PADonatedResource> = {
      projectId, organizationId,
      resourceType: form.resourceType, donorName: form.donorName, donorOrganization: form.donorOrganization,
      workDate: form.workDate, hoursWorked: form.hoursWorked, description: form.description,
      estimatedValue: stripCurrency(form.estimatedValue), notes: form.notes,
    };
    try {
      if (editing) await updateDonated(editing.Rid, data);
      else await createDonated(data);
      notifications.show({ title: 'Saved', message: 'Donated resource record saved.', color: 'green' });
      setModalOpen(false);
    } catch { notifications.show({ title: 'Error', message: 'Failed to save.', color: 'red' }); }
  }

  const totalValue = records.reduce((s, r) => s + (parseFloat(r.estimatedValue || '0') || 0), 0);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="xs"><Text fw={600} size="lg">Donated Resources</Text><Badge color="green" variant="light">Est. Value: {fmt(totalValue)}</Badge></Group>
        <Button color="green" leftSection={<IconPlus size={16} />} onClick={openAdd}>Add Donated Resource</Button>
      </Group>
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead><Table.Tr>
          <Table.Th>Type</Table.Th><Table.Th>Donor</Table.Th><Table.Th>Organization</Table.Th>
          <Table.Th>Date</Table.Th><Table.Th ta="right">Hours</Table.Th><Table.Th>Description</Table.Th>
          <Table.Th ta="right">Est. Value</Table.Th><Table.Th w={50}></Table.Th>
        </Table.Tr></Table.Thead>
        <Table.Tbody>
          {records.length === 0 && <Table.Tr><Table.Td colSpan={8}><Text ta="center" c="dimmed" py="md">No donated resources yet.</Text></Table.Td></Table.Tr>}
          {records.map(r => (
            <Table.Tr key={r.Rid} style={{ cursor: 'pointer' }} onClick={() => openEdit(r)}>
              <Table.Td fw={500}>{r.resourceType || '--'}</Table.Td>
              <Table.Td>{r.donorName || '--'}</Table.Td>
              <Table.Td>{r.donorOrganization || '--'}</Table.Td>
              <Table.Td>{r.workDate || '--'}</Table.Td>
              <Table.Td ta="right">{r.hoursWorked || '--'}</Table.Td>
              <Table.Td><Text size="sm" lineClamp={1}>{r.description || '--'}</Text></Table.Td>
              <Table.Td ta="right" fw={600}>{r.estimatedValue ? formatCurrency(r.estimatedValue) : '--'}</Table.Td>
              <Table.Td><ActionIcon variant="subtle" color="red" size="sm" onClick={e => { e.stopPropagation(); deleteDonated(r.Rid); }}><IconTrash size={14} /></ActionIcon></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Donated Resource' : 'Add Donated Resource'} size="lg">
        <Stack gap="sm">
          <Group grow>
            <TextInput label="Resource Type" required value={form.resourceType} onChange={e => setForm(f => ({ ...f, resourceType: e.currentTarget.value }))} placeholder="e.g. Labor, Equipment, Materials" />
            <TextInput label="Work Date" type="date" value={form.workDate} onChange={e => setForm(f => ({ ...f, workDate: e.currentTarget.value }))} />
          </Group>
          <Group grow>
            <TextInput label="Donor Name" value={form.donorName} onChange={e => setForm(f => ({ ...f, donorName: e.currentTarget.value }))} />
            <TextInput label="Donor Organization" value={form.donorOrganization} onChange={e => setForm(f => ({ ...f, donorOrganization: e.currentTarget.value }))} />
          </Group>
          <Group grow>
            <TextInput label="Hours Worked" value={form.hoursWorked} onChange={e => setForm(f => ({ ...f, hoursWorked: e.currentTarget.value }))} />
            <TextInput label="Estimated Value" value={formatCurrency(form.estimatedValue)} onChange={e => setForm(f => ({ ...f, estimatedValue: stripCurrency(e.currentTarget.value) }))} styles={{ input: { fontFamily: 'monospace' } }} />
          </Group>
          <Textarea label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.currentTarget.value }))} />
          <Textarea label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.currentTarget.value }))} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button color="green" onClick={handleSave}>{editing ? 'Save' : 'Add'}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
